import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CloudUpload as DeployIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

import { api, ensureUiScope } from '../services/api';
import { camerasApi, Camera } from '../services/camerasApi';
import { devicesApi } from '../services/devicesApi';
import { zonesApi, Zone } from '../services/zonesApi';
import { linesApi, Line } from '../services/linesApi';
import { zoneTemplatesApi, ZoneTemplate } from '../services/zoneTemplatesApi';
import * as mjpegApi from '../services/mjpegApi';
import { preferredMjpegModeForCamera as preferredMjpegModeForCameraId } from '../services/mjpegMode';

type DragMode = 'idle' | 'creating' | 'moving' | 'resizing' | 'rotating';
type Corner = 'nw' | 'ne' | 'sw' | 'se';
type GeometryMode = 'rect' | 'poly' | 'line';
type EditorTab = 'zones' | 'lines';

const SUPPORTED_ZONE_COLORS: Array<{ name: string; value: string }> = [
  { name: 'Cyan', value: '#00FFFF' },
  { name: 'Magenta', value: '#FF00FF' },
];

const SUPPORTED_LINE_THICKNESS: Array<{ name: string; value: number }> = [
  { name: 'Thin (3px)', value: 3 },
  { name: 'Medium (6px)', value: 6 },
  { name: 'Thick (10px)', value: 10 },
];

const VERTEX_HANDLE_RADIUS = 8;
const RESIZE_HANDLE_RADIUS = 8;
const ROTATE_HANDLE_RADIUS = 10;
const ROTATE_HANDLE_OFFSET_PX = 40;

type LocalZone = Zone & {
  // When creating a brand new zone we may not have a DB uuid yet.
  _local_new?: boolean;
  // Track whether the current zone_id is still auto-derived from name.
  _auto_zone_id?: boolean;
};

type LocalLine = Line & {
  _local_new?: boolean;
};

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function normToPx(b: number[], w: number, h: number): { x: number; y: number; ww: number; hh: number } {
  const [nx, ny, nw, nh] = b;
  return { x: nx * w, y: ny * h, ww: nw * w, hh: nh * h };
}

function pxToNorm(x: number, y: number, ww: number, hh: number, w: number, h: number): number[] {
  const nx = clamp(x / w, 0, 1);
  const ny = clamp(y / h, 0, 1);
  const nw = clamp(ww / w, 0, 1);
  const nh = clamp(hh / h, 0, 1);
  // Ensure still within bounds
  const nw2 = clamp(nw, 0, 1 - nx);
  const nh2 = clamp(nh, 0, 1 - ny);
  return [nx, ny, nw2, nh2];
}

function handlePoints(rect: { x: number; y: number; ww: number; hh: number }): Record<Corner, { x: number; y: number }> {
  return {
    nw: { x: rect.x, y: rect.y },
    ne: { x: rect.x + rect.ww, y: rect.y },
    sw: { x: rect.x, y: rect.y + rect.hh },
    se: { x: rect.x + rect.ww, y: rect.y + rect.hh },
  };
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function pxToNormPoint(x: number, y: number, w: number, h: number): [number, number] {
  return [clamp(x / w, 0, 1), clamp(y / h, 0, 1)];
}

function normToPxPoint(pt: [number, number], w: number, h: number): { x: number; y: number } {
  return { x: pt[0] * w, y: pt[1] * h };
}

function bboxFromPoly(poly: number[][]): number[] {
  const xs = poly.map((p) => p[0]);
  const ys = poly.map((p) => p[1]);
  const x1 = Math.min(...xs);
  const x2 = Math.max(...xs);
  const y1 = Math.min(...ys);
  const y2 = Math.max(...ys);
  const ww = Math.max(0, x2 - x1);
  const hh = Math.max(0, y2 - y1);
  return [x1, y1, ww, hh];
}

function pointInPolygon(pt: { x: number; y: number }, polyPx: Array<{ x: number; y: number }>): boolean {
  // Ray casting
  let inside = false;
  for (let i = 0, j = polyPx.length - 1; i < polyPx.length; j = i++) {
    const xi = polyPx[i].x;
    const yi = polyPx[i].y;
    const xj = polyPx[j].x;
    const yj = polyPx[j].y;
    const intersect = (yi > pt.y) !== (yj > pt.y) && pt.x < ((xj - xi) * (pt.y - yi)) / ((yj - yi) || 1e-9) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function rectPolygonFromBBoxNorm(bbox: number[], w: number, h: number): Array<{ x: number; y: number }> {
  const r = normToPx(bbox || [0, 0, 0, 0], w, h);
  const x1 = r.x;
  const y1 = r.y;
  const x2 = r.x + r.ww;
  const y2 = r.y + r.hh;
  return [
    { x: x1, y: y1 },
    { x: x2, y: y1 },
    { x: x2, y: y2 },
    { x: x1, y: y2 },
  ];
}

function polygonCenter(polyPx: Array<{ x: number; y: number }>): { x: number; y: number } {
  if (!Array.isArray(polyPx) || polyPx.length === 0) return { x: 0, y: 0 };
  const sum = polyPx.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 }
  );
  return { x: sum.x / polyPx.length, y: sum.y / polyPx.length };
}

function rotatePointAround(
  point: { x: number; y: number },
  center: { x: number; y: number },
  angleRad: number
): { x: number; y: number } {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

function rotateHandleForPolygon(polyPx: Array<{ x: number; y: number }>): { x: number; y: number } {
  const ys = polyPx.map((p) => p.y);
  const minY = Math.min(...ys);
  const topPoints = polyPx.filter((p) => Math.abs(p.y - minY) < 1e-6);
  const anchor = topPoints.length > 0 ? polygonCenter(topPoints) : polygonCenter(polyPx);
  return { x: anchor.x, y: anchor.y - ROTATE_HANDLE_OFFSET_PX };
}

function polygonAngleDeg(polyPx: Array<{ x: number; y: number }>): number {
  if (!Array.isArray(polyPx) || polyPx.length < 2) return 0;
  const p0 = polyPx[0];
  const p1 = polyPx[1];
  const angle = Math.atan2(p1.y - p0.y, p1.x - p0.x);
  return (angle * 180) / Math.PI;
}

function rectCornerToPolyIndex(corner: Corner | null): number | null {
  switch (corner) {
    case 'nw':
      return 0;
    case 'ne':
      return 1;
    case 'se':
      return 2;
    case 'sw':
      return 3;
    default:
      return null;
  }
}

function snapAngleDelta(deltaRad: number, stepDeg: number): number {
  const stepRad = (Math.PI * stepDeg) / 180;
  if (!Number.isFinite(stepRad) || stepRad <= 0) return deltaRad;
  return Math.round(deltaRad / stepRad) * stepRad;
}

export function ZoneSnapshotEditorDialog(props: {
  open: boolean;
  onClose: () => void;
  siteId: string;
  initialEdgeDeviceId?: string;
  initialCameraId?: string;
  /** When opening the editor, start in fullscreen mode (recommended for drawing). */
  initialFullScreen?: boolean;
}) {
  const { open, onClose, siteId, initialEdgeDeviceId, initialCameraId, initialFullScreen } = props;

  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [isMaximized, setIsMaximized] = useState<boolean>(Boolean(initialFullScreen));

  // Apply initial fullscreen preference when dialog is opened.
  useEffect(() => {
    if (!open) return;
    setIsMaximized(Boolean(initialFullScreen));
  }, [open, initialFullScreen]);

  const [cameras, setCameras] = useState<Camera[]>([]);
  const [devicesFromApi, setDevicesFromApi] = useState<string[]>([]);
  const [edgeCamerasForDevice, setEdgeCamerasForDevice] = useState<Camera[]>([]);
  const deviceIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...cameras.map((c) => c.edge_device_id),
          ...devicesFromApi,
        ])
      ).filter(Boolean).sort(),
    [cameras, devicesFromApi]
  );

  const [deviceId, setDeviceId] = useState<string>('');
  const [cameraId, setCameraId] = useState<string>('');
  // F-09: Lock camera once geometry is selected to prevent cross-camera confusion
  const [cameraLocked, setCameraLocked] = useState<boolean>(false);

  const camerasForDevice = useMemo(() => {
    const fromDb = cameras.filter((c) => c.edge_device_id === deviceId);
    if (fromDb.length > 0) return fromDb;
    return edgeCamerasForDevice;
  }, [cameras, deviceId, edgeCamerasForDevice]);

  const [snapshotDataUrl, setSnapshotDataUrl] = useState<string>('');
  const [snapshotLoading, setSnapshotLoading] = useState<boolean>(false);
  // F-08: Track snapshot fetch time so we can show Fresh/Stale and auto-refresh when stale.
  const [snapshotFetchedAtMs, setSnapshotFetchedAtMs] = useState<number | null>(null);
  const [snapshotSource, setSnapshotSource] = useState<'mjpeg' | 'forced' | null>(null);
  const snapshotReqNonceRef = useRef<number>(0);
  const snapshotBlobUrlRef = useRef<string | null>(null);

  const [zones, setZones] = useState<LocalZone[]>([]);
  const [zonesLoading, setZonesLoading] = useState<boolean>(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [showDisabledZones, setShowDisabledZones] = useState<boolean>(false);

  const [lines, setLines] = useState<LocalLine[]>([]);
  const [linesLoading, setLinesLoading] = useState<boolean>(false);
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [showDisabledLines, setShowDisabledLines] = useState<boolean>(false);
  const [tab, setTab] = useState<EditorTab>('zones');
  const [showZoneAdvanced, setShowZoneAdvanced] = useState<boolean>(false);

  // Zone templates (Phase G)
  const [zoneTemplates, setZoneTemplates] = useState<ZoneTemplate[]>([]);
  const [zoneTemplatesLoading, setZoneTemplatesLoading] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [saveTemplateOpen, setSaveTemplateOpen] = useState<boolean>(false);
  const [saveTemplateName, setSaveTemplateName] = useState<string>('');
  const [saveTemplateDescription, setSaveTemplateDescription] = useState<string>('');

  const selectedZone = useMemo(() => zones.find((z) => z.id === selectedZoneId) || null, [zones, selectedZoneId]);
  const selectedLine = useMemo(() => lines.find((l) => l.id === selectedLineId) || null, [lines, selectedLineId]);
  const geometryAuthoringReady = Boolean(snapshotDataUrl) && snapshotSource === 'mjpeg';

  const [validation, setValidation] = useState<any>(null);
  const [action, setAction] = useState<'idle' | 'validating' | 'deploying'>('idle');
  const actionBusy = action !== 'idle';
  const [draftSaveState, setDraftSaveState] = useState<'never' | 'saved' | 'error'>('never');
  const [lastDraftSavedAtMs, setLastDraftSavedAtMs] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [dragMode, setDragMode] = useState<DragMode>('idle');
  const [activeCorner, setActiveCorner] = useState<Corner | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [origBBox, setOrigBBox] = useState<number[] | null>(null);
  const [geometryMode, setGeometryMode] = useState<GeometryMode>('rect');
  const [polyDraft, setPolyDraft] = useState<Array<{ x: number; y: number }> | null>(null); // px points while drawing
  const [polyDragIndex, setPolyDragIndex] = useState<number | null>(null); // vertex index; null => move whole polygon
  const [polyDragOrigin, setPolyDragOrigin] = useState<Array<{ x: number; y: number }> | null>(null); // px points at drag start
  const [rotateDragOrigin, setRotateDragOrigin] = useState<Array<{ x: number; y: number }> | null>(null); // px points at rotate start
  const [rotateCenter, setRotateCenter] = useState<{ x: number; y: number } | null>(null);
  const [rotateStartAngle, setRotateStartAngle] = useState<number | null>(null);
  const [lineDraft, setLineDraft] = useState<Array<{ x: number; y: number }> | null>(null); // two points px
  const [lineDragIndex, setLineDragIndex] = useState<number | null>(null); // 0/1 endpoint; null => move
  const [lineDragOrigin, setLineDragOrigin] = useState<Array<{ x: number; y: number }> | null>(null);
  const [rotationSnapStepDeg, setRotationSnapStepDeg] = useState<number>(15);

  const [pendingNameFocus, setPendingNameFocus] = useState<boolean>(false);

  const POLY_CLOSE_PX = 12;
  // When snapshot source swaps (thumbnail -> mjpeg -> forced), img.clientWidth can temporarily be 0.
  // Use normalized threshold for "too small" zones so newly drawn zones don't disappear.
  const MIN_DRAW_NORM = 0.015; // ~1.5% of frame dimension (≈12px at 800px wide)

  const dragModeRef = useRef<DragMode>('idle');
  useEffect(() => {
    dragModeRef.current = dragMode;
  }, [dragMode]);
  const hasUnsavedEdits = useMemo(() => {
    const hasUnsavedZones = (zones || []).some((z: any) => Boolean(z?._local_new) || String(z?.id || '').startsWith('local_'));
    const hasUnsavedLines = (lines || []).some((l: any) => Boolean(l?._local_new) || String(l?.id || '').startsWith('local_line_'));
    return hasUnsavedZones || hasUnsavedLines;
  }, [zones, lines]);
  const hasUnsavedEditsRef = useRef<boolean>(false);
  useEffect(() => {
    hasUnsavedEditsRef.current = hasUnsavedEdits;
  }, [hasUnsavedEdits]);

  useEffect(() => {
    if (!open) return;
    setDraftSaveState('never');
    setLastDraftSavedAtMs(null);
  }, [open, deviceId, cameraId]);

  const ensureGeometryAuthoringReady = useCallback(
    (intent: string): boolean => {
      if (geometryAuthoringReady) return true;
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          snapshotSource === 'forced'
            ? `${intent} is disabled while the editor is using a fallback snapshot. Refresh until you see "Edge live framing" before editing geometry.`
            : `${intent} is disabled until the editor has an authoritative live frame. Refresh the snapshot and wait for "Edge live framing".`,
      });
      return false;
    },
    [geometryAuthoringReady, snapshotSource]
  );

  function slugify(s: string): string {
    const out = (s || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64);
    return out;
  }

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    const w = img.clientWidth;
    const h = img.clientHeight;
    
    // Wayland fix: Ensure canvas has valid dimensions before drawing
    if (w === 0 || h === 0) {
      // Image not loaded yet, wait for next frame
      if (process.env.NODE_ENV === 'development') {
        console.debug('Canvas draw skipped: image dimensions are 0', { w, h });
      }
      return;
    }
    
    // Set canvas size (this clears the canvas on Wayland/compositor changes)
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);

    for (const z of zones) {
      const poly = (z as any).polygon_norm as number[][] | undefined;
      const bbox = z.bbox_norm || (Array.isArray(poly) && poly.length >= 3 ? bboxFromPoly(poly) : [0, 0, 0, 0]);
      if (!Array.isArray(bbox) || bbox.length !== 4) continue;
      const r = normToPx(bbox, w, h);
      const isSel = z.id === selectedZoneId;
      const stroke = z.color || '#00FFFF';
      const alpha = z.enabled === false ? 0.25 : 0.6;

      ctx.save();
      ctx.globalAlpha = 1.0;
      ctx.lineWidth = isSel ? 3 : 2;
      ctx.strokeStyle = stroke;
      ctx.setLineDash(z.enabled === false ? [6, 4] : []);
      if (Array.isArray(poly) && poly.length >= 3) {
        const pts = poly.map((p) => normToPxPoint([p[0], p[1]], w, h));
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.closePath();
        ctx.stroke();

        if (z.enabled !== false) {
          ctx.globalAlpha = alpha;
          ctx.fillStyle = stroke;
          ctx.fill();
          ctx.globalAlpha = 1.0;
        }

        if (isSel) {
          ctx.fillStyle = '#FFFFFF';
          ctx.strokeStyle = stroke;
          ctx.lineWidth = 2;
          for (const p of pts) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, VERTEX_HANDLE_RADIUS, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
          const rHandle = rotateHandleForPolygon(pts);
          const c = polygonCenter(pts);
          ctx.beginPath();
          ctx.moveTo(c.x, c.y);
          ctx.lineTo(rHandle.x, rHandle.y);
          ctx.strokeStyle = '#FFA000';
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(rHandle.x, rHandle.y, ROTATE_HANDLE_RADIUS, 0, Math.PI * 2);
          ctx.fillStyle = '#FFFFFF';
          ctx.fill();
          ctx.strokeStyle = '#FFA000';
          ctx.stroke();
          ctx.fillStyle = '#FFA000';
          ctx.font = 'bold 12px sans-serif';
          ctx.fillText('rotate', rHandle.x - 18, rHandle.y - ROTATE_HANDLE_RADIUS - 6);
        }
      } else {
        ctx.strokeRect(r.x, r.y, r.ww, r.hh);

        // light fill for enabled zones
        if (z.enabled !== false) {
          ctx.globalAlpha = alpha;
          ctx.fillStyle = stroke;
          ctx.fillRect(r.x, r.y, r.ww, r.hh);
        }

        // handles for selected
        if (isSel) {
          const hp = handlePoints(r);
          ctx.globalAlpha = 1.0;
          ctx.fillStyle = '#FFFFFF';
          ctx.strokeStyle = stroke;
          ctx.lineWidth = 2;
          for (const p of Object.values(hp)) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, RESIZE_HANDLE_RADIUS, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
          const handleAnchor = { x: r.x + r.ww / 2, y: r.y };
          const rHandle = { x: handleAnchor.x, y: handleAnchor.y - ROTATE_HANDLE_OFFSET_PX };
          ctx.beginPath();
          ctx.moveTo(handleAnchor.x, handleAnchor.y);
          ctx.lineTo(rHandle.x, rHandle.y);
          ctx.strokeStyle = '#FFA000';
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(rHandle.x, rHandle.y, ROTATE_HANDLE_RADIUS, 0, Math.PI * 2);
          ctx.fillStyle = '#FFFFFF';
          ctx.fill();
          ctx.strokeStyle = '#FFA000';
          ctx.stroke();
          ctx.fillStyle = '#FFA000';
          ctx.font = 'bold 12px sans-serif';
          ctx.fillText('rotate', rHandle.x - 18, rHandle.y - ROTATE_HANDLE_RADIUS - 6);
        }
      }

      // label (use bbox placement)
      ctx.globalAlpha = 1.0;
      ctx.fillStyle = stroke;
      ctx.font = '12px sans-serif';
      const label = z.name || z.zone_id || 'Zone';
      ctx.fillText(label, r.x + 4, r.y + 14);

      ctx.restore();
    }

    // Draw lines
    if (process.env.NODE_ENV === 'development') {
      console.debug(`🎨 Drawing ${lines.length} lines on canvas (Wayland)`, {
        canvas_size: { w, h },
        lines: lines.map(l => ({ 
          name: l.name, 
          line_id: l.line_id, 
          id: l.id,
          has_points: Array.isArray(l.points_norm) && l.points_norm.length === 2,
          enabled: l.enabled,
          points_norm: l.points_norm
        }))
      });
    }
    
    for (const l of lines) {
      const pts = (l.points_norm || []) as number[][];
      if (!Array.isArray(pts) || pts.length !== 2) {
        // Debug: log why line is skipped
        if (process.env.NODE_ENV === 'development') {
          console.debug(`⏭️ Skipping line ${l.name || l.line_id || l.id}: invalid points_norm`, {
            name: l.name,
            line_id: l.line_id,
            id: l.id,
            points_norm: pts,
            isArray: Array.isArray(pts),
            length: Array.isArray(pts) ? pts.length : 'not array'
          });
        }
        continue;
      }
      
      // Validate that both points are valid arrays with 2 numbers
      if (!Array.isArray(pts[0]) || pts[0].length !== 2 || 
          !Array.isArray(pts[1]) || pts[1].length !== 2) {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`Skipping line ${l.name || l.line_id || l.id}: invalid point structure`, {
            name: l.name,
            line_id: l.line_id,
            point0: pts[0],
            point1: pts[1]
          });
        }
        continue;
      }
      
      const a = normToPxPoint([pts[0][0], pts[0][1]], w, h);
      const b = normToPxPoint([pts[1][0], pts[1][1]], w, h);
      const stroke = l.color || '#00FFFF';
      const isSel = l.id === selectedLineId;
      const baseWidth = Math.max(1, Math.min(50, Number((l as any).thickness_px ?? 6)));

      ctx.save();
      ctx.globalAlpha = l.enabled === false ? 0.35 : 1.0;
      // Visibility: lines can blend into filled zones (same default cyan). Draw a subtle outline first.
      const lineWidth = isSel ? baseWidth + 1 : baseWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineWidth = Math.min(60, lineWidth + 4);
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = stroke;
      ctx.stroke();

      // endpoints (handles)
      if (isSel) {
        ctx.fillStyle = '#FFFFFF';
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 2;
        for (const p of [a, b]) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }

      // label
      const label = l.name || l.line_id || 'Line';
      ctx.font = '12px sans-serif';
      const tx = (a.x + b.x) / 2 + 6;
      const ty = (a.y + b.y) / 2 + 6;
      // Text outline so label stays readable on bright overlays
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.strokeText(label, tx, ty);
      ctx.fillStyle = stroke;
      ctx.fillText(label, tx, ty);

      ctx.restore();
    }

    // polygon draft (in-progress)
    if (polyDraft && polyDraft.length > 0) {
      ctx.save();
      ctx.globalAlpha = 1.0;
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#00FFFF';
      ctx.fillStyle = '#00FFFF';
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(polyDraft[0].x, polyDraft[0].y);
      for (let i = 1; i < polyDraft.length; i++) ctx.lineTo(polyDraft[i].x, polyDraft[i].y);
      ctx.stroke();
      // draw points
      ctx.setLineDash([]);
      for (const p of polyDraft) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // line draft (in-progress)
    if (lineDraft && lineDraft.length > 0) {
      ctx.save();
      ctx.globalAlpha = 1.0;
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#00FFFF';
      ctx.setLineDash([6, 4]);
      if (lineDraft.length === 1) {
        ctx.beginPath();
        ctx.arc(lineDraft[0].x, lineDraft[0].y, 4, 0, Math.PI * 2);
        ctx.stroke();
      } else if (lineDraft.length === 2) {
        ctx.beginPath();
        ctx.moveTo(lineDraft[0].x, lineDraft[0].y);
        ctx.lineTo(lineDraft[1].x, lineDraft[1].y);
        ctx.stroke();
      }
      ctx.restore();
    }
  }, [zones, selectedZoneId, polyDraft, lines, selectedLineId, lineDraft]);

  useEffect(() => {
    // Force redraw when snapshot loads or changes
    // Use requestAnimationFrame to ensure canvas is ready (especially on Wayland)
    const rafId = requestAnimationFrame(() => {
      draw();
    });
    return () => cancelAnimationFrame(rafId);
  }, [draw, snapshotDataUrl]);

  // Avoid leaking blob: URLs when we replace snapshots repeatedly.
  useEffect(() => {
    return () => {
      if (snapshotBlobUrlRef.current) {
        try {
          URL.revokeObjectURL(snapshotBlobUrlRef.current);
        } catch {
          // ignore
        }
        snapshotBlobUrlRef.current = null;
      }
    };
  }, []);

  // Also redraw when lines/zones change (in case draw dependency doesn't catch it)
  useEffect(() => {
    if (snapshotDataUrl) {
      const rafId = requestAnimationFrame(() => {
        draw();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [lines, zones, draw, snapshotDataUrl]);

  const preferredMjpegModeForCamera = useCallback((camId: string): 'stable' | 'low_latency' => {
    // Keep Zone Editor aligned with Live View (stable for looped MediaMTX demos).
    return preferredMjpegModeForCameraId(camId, 'live');
  }, []);

  const refreshSnapshot = useCallback(async (opts?: { userInitiated?: boolean }) => {
    if (!deviceId || !cameraId) return;
    const editorViewKind: 'live' = 'live';
    const canReplaceSnapshotNow = () =>
      dragModeRef.current === 'idle' && !hasUnsavedEditsRef.current;
    if (hasUnsavedEditsRef.current) {
      if (!opts?.userInitiated) {
        // Never auto-refresh when there are unsaved edits; it can make in-progress work feel unstable.
        return;
      }
      const ok = window.confirm(
        'You have unsaved edits. Refreshing snapshot can visually reset your in-progress drawing.\n\nRefresh anyway?'
      );
      if (!ok) return;
    }
    const reqNonce = Date.now();
    snapshotReqNonceRef.current = reqNonce;
    setSnapshotLoading(true);
    try {
      // Geometry authoring should prefer the edge-rendered live output so the
      // snapshot editor matches the framing used by edge live/annotated streams.
      // Do not use the thumbnail-first shortcut here: it can come from a
      // different camera/source path and cause zone coordinate drift.
      let gotMjpeg = false;

      // 1) Edge live MJPEG frame (authoritative framing if available)
      try {
        // Don't swap snapshot source while the user is actively drawing/dragging;
        // it can cause img dimensions to transiently become 0 and lose the in-progress temp zone.
        if (dragModeRef.current !== 'idle') return;
        const mjpegMode = preferredMjpegModeForCamera(cameraId);
        await mjpegApi.startStream(
          cameraId,
          undefined,
          deviceId,
          mjpegMode,
          editorViewKind
        );
        // Stable / looped demo relays often need >8s to emit the first real JPEG.
        const warmupMs =
          cameraId === 'hardware_cam' || mjpegMode === 'stable' ? 30_000 : 12_000;
        const deadlineMs = Date.now() + warmupMs;
        let realStreak = 0;
        while (Date.now() < deadlineMs) {
          if (snapshotReqNonceRef.current !== reqNonce) return;
          if (!canReplaceSnapshotNow()) break;
          try {
            const snapshot = await mjpegApi.getSnapshot(cameraId, deviceId, editorViewKind);
            if (mjpegApi.isLikelyRealMjpegSnapshot(snapshot)) {
              realStreak += 1;
            } else {
              realStreak = 0;
            }
            if (realStreak >= mjpegApi.MJPEG_LIVE_READY_SNAPSHOT_STREAK) {
              if (!canReplaceSnapshotNow()) break;
              const bytes = snapshot.bytes;
              // Some environments type `bytes` as Uint8Array<SharedArrayBufferLike>.
              // BlobPart does not accept SharedArrayBuffer, so force a copy into a new ArrayBuffer.
              const copy = new Uint8Array(bytes.length);
              copy.set(bytes);
              const blob = new Blob([copy.buffer], { type: 'image/jpeg' });
              const url = URL.createObjectURL(blob);
              if (snapshotBlobUrlRef.current) {
                try {
                  URL.revokeObjectURL(snapshotBlobUrlRef.current);
                } catch {
                  // ignore
                }
              }
              snapshotBlobUrlRef.current = url;
              setSnapshotDataUrl(url);
              setSnapshotFetchedAtMs(Date.now());
              setSnapshotSource('mjpeg');
              gotMjpeg = true;
              break;
            }
          } catch {
            // not ready yet (404) or transient; retry
            realStreak = 0;
          }
          await new Promise((r) => setTimeout(r, 600));
        }
      } catch {
        // ignore (fallback to forced snapshot)
      }

      // 2) Forced edge snapshot fallback. This is useful for recovery but may
      // not perfectly match the edge live framing if the cloud falls back to a
      // different snapshot path.
      try {
        if (snapshotReqNonceRef.current !== reqNonce) return;
        if (dragModeRef.current !== 'idle') return;
        if (!canReplaceSnapshotNow()) return;
        const shouldForce = !gotMjpeg;
        if (shouldForce) {
          const b64 = await camerasApi.getCameraSnapshot(cameraId, deviceId);
          if (snapshotReqNonceRef.current !== reqNonce) return;
          if (!canReplaceSnapshotNow()) return;
          if (snapshotBlobUrlRef.current) {
            try {
              URL.revokeObjectURL(snapshotBlobUrlRef.current);
            } catch {
              // ignore
            }
            snapshotBlobUrlRef.current = null;
          }
          setSnapshotDataUrl(b64);
          setSnapshotFetchedAtMs(Date.now());
          setSnapshotSource('forced');
        }
      } catch {
        // If even forced snapshot fails, keep whatever we had.
      }
    } catch {
      if (snapshotReqNonceRef.current !== reqNonce) return;
      setSnapshotDataUrl('');
      setSnapshotFetchedAtMs(null);
      setSnapshotSource(null);
    } finally {
      if (snapshotReqNonceRef.current === reqNonce) {
        setSnapshotLoading(false);
      }
    }
  }, [cameraId, deviceId, preferredMjpegModeForCamera]);

  const refreshZones = useCallback(async (opts?: { preserveLocal?: boolean }) => {
    if (!deviceId || !cameraId) return;
    const preserveLocal = opts?.preserveLocal !== false;
    setZonesLoading(true);
    try {
      const res = await zonesApi.listZones(undefined, undefined, deviceId, cameraId, showDisabledZones);
      const items = (res.zones || []) as Zone[];
      setZones((prev) => {
        if (!preserveLocal) {
          return [...(items as LocalZone[])];
        }
        // Preserve any unsaved local zones (drawn but not saved yet).
        // Be defensive: keep both `_local_new` and `local_*` ids so an accidental state
        // overwrite doesn't make freshly drawn zones "disappear" from the list.
        const locals = (prev || []).filter((z: any) => Boolean(z?._local_new) || String(z?.id || '').startsWith('local_')) as LocalZone[];
        const merged: LocalZone[] = [...(items as LocalZone[])];
        const existingZoneIds = new Set<string>(merged.map((z) => String(z.zone_id || '')));
        for (const l of locals) {
          const zid = String(l.zone_id || '');
          // Keep if it doesn't collide with a DB zone_id.
          if (!zid || !existingZoneIds.has(zid)) {
            merged.push(l);
          }
        }
        return merged;
      });
      const dbZoneIds = new Set<string>((items || []).map((z: any) => String(z?.id || '')));
      setSelectedZoneId((prevSel) => {
        const prev = String(prevSel || '');
        if (prev && (preserveLocal || dbZoneIds.has(prev))) return prev;
        if (items.length > 0) return String(items[0].id);
        return '';
      });
    } finally {
      setZonesLoading(false);
    }
  }, [cameraId, deviceId, showDisabledZones]);

  const refreshLines = useCallback(async (opts?: { preserveLocal?: boolean }) => {
    if (!deviceId || !cameraId) return;
    const preserveLocal = opts?.preserveLocal !== false;
    setLinesLoading(true);
    try {
      const res = await linesApi.listLines(deviceId, cameraId, showDisabledLines);
      const items = (res.lines || []) as Line[];
      
      // Debug: log loaded lines
      if (process.env.NODE_ENV === 'development') {
        console.log(`📋 Loaded ${items.length} lines from DB for ${cameraId}:`, 
          items.map(l => ({ 
            name: l.name, 
            line_id: l.line_id, 
            id: l.id, 
            points_count: Array.isArray(l.points_norm) ? l.points_norm.length : 'invalid',
            enabled: l.enabled
          }))
        );
      }
      
      setLines((prev) => {
        if (!preserveLocal) {
          return [...(items as LocalLine[])];
        }
        // Preserve any truly local (unsaved) lines
        const locals = (prev || []).filter((l) => {
          const isLocal = (l as any)?._local_new;
          const hasLocalId = String(l.id || '').startsWith('local_line_');
          return isLocal || hasLocalId;
        }) as LocalLine[];
        
        // Start with DB lines
        const merged: LocalLine[] = [...(items as LocalLine[])];
        
        // Track existing line_ids and ids to avoid duplicates
        const existingLineIds = new Set<string>(merged.map((l) => String(l.line_id || '')));
        const existingIds = new Set<string>(merged.map((l) => String(l.id || '')));
        
        // Add local lines that aren't already in DB (by line_id or id)
        for (const l of locals) {
          const lid = String(l.line_id || '');
          const id = String(l.id || '');
          // Only add if it's truly new (not already saved to DB)
          if (lid && !existingLineIds.has(lid) && !existingIds.has(id)) {
            merged.push(l);
          }
        }
        
        // Debug: log final merged lines
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Final merged lines (${merged.length} total):`, 
            merged.map(l => ({ 
              name: l.name, 
              line_id: l.line_id, 
              id: l.id, 
              enabled: l.enabled,
              points_valid: Array.isArray(l.points_norm) && l.points_norm.length === 2
            }))
          );
        }
        
        return merged;
      });
      const dbLineIds = new Set<string>((items || []).map((l: any) => String(l?.id || '')));
      setSelectedLineId((prevSel) => {
        const prev = String(prevSel || '');
        if (!prev) return prevSel;
        if (preserveLocal || dbLineIds.has(prev)) return prevSel;
        return '';
      });
    } catch (error) {
      console.error('Failed to refresh lines:', error);
    } finally {
      setLinesLoading(false);
    }
  }, [cameraId, deviceId, showDisabledLines]);

  const loadCameras = useCallback(async () => {
    const [camRes, devIds] = await Promise.all([
      camerasApi.listCameras(siteId),
      devicesApi.listDeviceIdsForSite().catch(() => []),
    ]);
    const items = (camRes.items || []) as Camera[];
    setCameras(items);
    setDevicesFromApi(Array.isArray(devIds) ? devIds : []);

    if (!deviceId && (items.length > 0 || devIds.length > 0)) {
      const wantDev = (initialEdgeDeviceId || '').trim();
      const wantCam = (initialCameraId || '').trim();
      if (wantDev) {
        setDeviceId(wantDev);
        if (wantCam) {
          setCameraId(wantCam);
        } else {
          const firstCam = items.find((c) => c.edge_device_id === wantDev);
          setCameraId(firstCam?.camera_id || items[0]?.camera_id || '');
        }
      } else {
        const firstDev = items[0]?.edge_device_id || devIds[0];
        const firstCam = items[0]?.camera_id;
        if (firstDev) {
          setDeviceId(firstDev);
          setCameraId(firstCam || '');
        }
      }
    }
  }, [deviceId, siteId, initialCameraId, initialEdgeDeviceId]);

  const refreshZoneTemplates = useCallback(async () => {
    if (!siteId) return;
    setZoneTemplatesLoading(true);
    try {
      const items = await zoneTemplatesApi.list(siteId);
      setZoneTemplates(items || []);
    } catch (e) {
      console.error('Failed to load zone templates:', e);
      setZoneTemplates([]);
    } finally {
      setZoneTemplatesLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!open) return;
    setValidation(null);
    void loadCameras();
  }, [open, loadCameras]);

  // For devices without DB cameras (e.g. rubik_pi with incidents but no Preflight), fetch cameras from edge runtime
  useEffect(() => {
    if (!open || !deviceId) {
      setEdgeCamerasForDevice([]);
      return;
    }
    const fromDb = cameras.filter((c) => c.edge_device_id === deviceId);
    if (fromDb.length > 0) {
      setEdgeCamerasForDevice([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(
          `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/pipelines/cameras/runtime`
        );
        if (cancelled) return;
        const data = res?.data;
        const list = data?.cameras ?? data?.items ?? (Array.isArray(data) ? data : []);
        const cams: Camera[] = (list as any[]).map((c: any) => ({
          camera_id: c.camera_id ?? c.id ?? String(c),
          edge_device_id: deviceId,
          name: c.display_name ?? c.camera_id ?? c.id ?? String(c),
          status: (c.alive === true ? 'running' : 'unknown') as Camera['status'],
        }));
        setEdgeCamerasForDevice(cams);
        if (cams.length > 0 && !cameraId) {
          setCameraId(cams[0].camera_id);
        }
      } catch {
        if (!cancelled) setEdgeCamerasForDevice([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, deviceId, cameraId, cameras]);

  useEffect(() => {
    if (!open) return;
    void refreshZoneTemplates();
  }, [open, refreshZoneTemplates]);

  useEffect(() => {
    if (!open) return;
    if (!deviceId || !cameraId) {
      setZones([]);
      setLines([]);
      setSelectedZoneId('');
      setSelectedLineId('');
      setSnapshotDataUrl('');
      setSnapshotFetchedAtMs(null);
      setSnapshotSource(null);
      return;
    }

    // Camera switches must be authoritative: do not carry local drafts or stale
    // snapshot frames across cameras.
    setZones([]);
    setLines([]);
    setSelectedZoneId('');
    setSelectedLineId('');
    setPolyDraft(null);
    setLineDraft(null);
    setDragMode('idle');
    setActiveCorner(null);
    setDragStart(null);
    setOrigBBox(null);
    setPolyDragIndex(null);
    setPolyDragOrigin(null);
    setRotateDragOrigin(null);
    setRotateCenter(null);
    setRotateStartAngle(null);
    setLineDragIndex(null);
    setLineDragOrigin(null);
    hasUnsavedEditsRef.current = false;

    snapshotReqNonceRef.current = Date.now();
    if (snapshotBlobUrlRef.current) {
      try {
        URL.revokeObjectURL(snapshotBlobUrlRef.current);
      } catch {
        // ignore
      }
      snapshotBlobUrlRef.current = null;
    }
    setSnapshotDataUrl('');
    setSnapshotFetchedAtMs(null);
    setSnapshotSource(null);

    void refreshZones({ preserveLocal: false });
    void refreshLines({ preserveLocal: false });
    void refreshSnapshot();
  }, [open, deviceId, cameraId, refreshZones, refreshLines, refreshSnapshot]);

  // F-09: Lock camera when geometry is *actively being edited* to prevent cross-camera confusion.
  // IMPORTANT: Do NOT lock just because the camera already has zones/lines; that makes the editor unusable.
  useEffect(() => {
    const hasActiveEdit =
      Boolean(selectedZoneId) ||
      Boolean(selectedLineId) ||
      (Array.isArray(polyDraft) && polyDraft.length > 0) ||
      (Array.isArray(lineDraft) && lineDraft.length > 0) ||
      dragMode !== 'idle';
    setCameraLocked(hasActiveEdit);
  }, [selectedZoneId, selectedLineId, polyDraft, lineDraft, dragMode]);

  const confirmUnlockAndClearEdits = useCallback((): boolean => {
    if (!cameraLocked) return true;
    const ok = window.confirm('Unlock editor and switch camera? Any in-progress drawing/selection will be cleared.');
    if (!ok) return false;

    // Clear local editing state so we don't carry selection/drafts across cameras.
    setSelectedZoneId('');
    setSelectedLineId('');
    setPolyDraft(null);
    setLineDraft(null);
    setDragMode('idle');
    setActiveCorner(null);
    setDragStart(null);
    setOrigBBox(null);
    setPolyDragIndex(null);
    setPolyDragOrigin(null);
    setRotateDragOrigin(null);
    setRotateCenter(null);
    setRotateStartAngle(null);
    setLineDragIndex(null);
    setLineDragOrigin(null);
    return true;
  }, [cameraLocked]);

  const getMouse = (evt: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
  };

  const hitTestLine = (pt: { x: number; y: number }) => {
    const img = imgRef.current;
    if (!img) return null;
    const w = img.clientWidth;
    const h = img.clientHeight;
    const radius = Math.max(VERTEX_HANDLE_RADIUS, RESIZE_HANDLE_RADIUS) + 4;

    // Prefer endpoints on selected line
    if (selectedLine) {
      const pts = selectedLine.points_norm || [];
      if (Array.isArray(pts) && pts.length === 2) {
        const a = normToPxPoint([pts[0][0], pts[0][1]], w, h);
        const b = normToPxPoint([pts[1][0], pts[1][1]], w, h);
        if (dist(pt, a) <= radius) return { lineId: selectedLine.id, idx: 0 };
        if (dist(pt, b) <= radius) return { lineId: selectedLine.id, idx: 1 };
      }
    }

    // Otherwise select any line by proximity to segment (simple bbox threshold)
    for (let i = lines.length - 1; i >= 0; i--) {
      const l = lines[i];
      const pts = l.points_norm || [];
      if (!Array.isArray(pts) || pts.length !== 2) continue;
      const a = normToPxPoint([pts[0][0], pts[0][1]], w, h);
      const b = normToPxPoint([pts[1][0], pts[1][1]], w, h);
      const minx = Math.min(a.x, b.x) - radius;
      const maxx = Math.max(a.x, b.x) + radius;
      const miny = Math.min(a.y, b.y) - radius;
      const maxy = Math.max(a.y, b.y) + radius;
      if (pt.x < minx || pt.x > maxx || pt.y < miny || pt.y > maxy) continue;
      return { lineId: l.id, idx: null };
    }
    return null;
  };

  const hitTest = (pt: { x: number; y: number }) => {
    const img = imgRef.current;
    if (!img) return null;
    const w = img.clientWidth;
    const h = img.clientHeight;
    const radius = Math.max(VERTEX_HANDLE_RADIUS, ROTATE_HANDLE_RADIUS, RESIZE_HANDLE_RADIUS) + 4;

    // Prefer handles on selected zone
    if (selectedZone) {
      const poly = (selectedZone as any).polygon_norm as number[][] | undefined;
      if (Array.isArray(poly) && poly.length >= 3) {
        const pts = poly.map((p) => normToPxPoint([p[0], p[1]], w, h));
        const rHandle = rotateHandleForPolygon(pts);
        if (dist(pt, rHandle) <= radius + 4) return { zoneId: selectedZone.id, corner: null, polyIndex: null, rotate: true };
        for (let i = 0; i < pts.length; i++) {
          if (dist(pt, pts[i]) <= radius) return { zoneId: selectedZone.id, corner: null, polyIndex: i };
        }
        if (pointInPolygon(pt, pts)) return { zoneId: selectedZone.id, corner: null, polyIndex: null };
      } else {
        const r = normToPx(selectedZone.bbox_norm || [0, 0, 0, 0], w, h);
        const rHandle = { x: r.x + r.ww / 2, y: r.y - ROTATE_HANDLE_OFFSET_PX };
        if (dist(pt, rHandle) <= radius + 4) return { zoneId: selectedZone.id, corner: null, polyIndex: null, rotate: true };
        const hp = handlePoints(r);
        for (const [corner, p] of Object.entries(hp) as Array<[Corner, { x: number; y: number }]> ) {
          if (dist(pt, p) <= radius) return { zoneId: selectedZone.id, corner };
        }
      }
    }

    // Otherwise hit any zone body
    for (let i = zones.length - 1; i >= 0; i--) {
      const z = zones[i];
      const poly = (z as any).polygon_norm as number[][] | undefined;
      if (Array.isArray(poly) && poly.length >= 3) {
        const pts = poly.map((p) => normToPxPoint([p[0], p[1]], w, h));
        if (pointInPolygon(pt, pts)) return { zoneId: z.id, corner: null, polyIndex: null };
      } else {
        const r = normToPx(z.bbox_norm || [0, 0, 0, 0], w, h);
        if (pt.x >= r.x && pt.x <= r.x + r.ww && pt.y >= r.y && pt.y <= r.y + r.hh) {
          return { zoneId: z.id, corner: null, polyIndex: null };
        }
      }
    }
    return null;
  };

  const onMouseDown = (evt: React.MouseEvent) => {
    const img = imgRef.current;
    if (!img) return;
    if (!ensureGeometryAuthoringReady('Geometry editing')) return;
    const w = img.clientWidth;
    const h = img.clientHeight;
    const pt = getMouse(evt);
    const hit: any = hitTest(pt);

    // Line mode: click 2 points to create; drag endpoints/move to edit.
    if (geometryMode === 'line') {
      if (lineDraft && lineDraft.length === 1) {
        const p2 = pt;
        const stableId = `local_line_${Date.now()}`;
        const a = pxToNormPoint(lineDraft[0].x, lineDraft[0].y, w, h);
        const b = pxToNormPoint(p2.x, p2.y, w, h);
        const pts = [
          [a[0], a[1]],
          [b[0], b[1]],
        ];
        setLines((prev) => [
          ...prev.filter((l) => l.id !== '__temp_line__'),
          {
            id: stableId,
            line_id: '',
            name: '',
            device_id: deviceId,
            camera_id: cameraId,
            points_norm: pts,
            color: '#00FFFF',
            thickness_px: 6,
            enabled: true,
            _local_new: true,
          } as any,
        ]);
        setSelectedLineId(stableId);
        setTab('lines');
        setLineDraft(null);
        return;
      }

      // Try select existing line for edit
      const lh: any = hitTestLine(pt);
      if (lh?.lineId) {
        setSelectedLineId(lh.lineId);
        setTab('lines');
        const l = lines.find((x) => x.id === lh.lineId);
        if (!l) return;
        const pts = l.points_norm || [];
        if (Array.isArray(pts) && pts.length === 2) {
          const a = normToPxPoint([pts[0][0], pts[0][1]], w, h);
          const b = normToPxPoint([pts[1][0], pts[1][1]], w, h);
          setLineDragOrigin([a, b]);
          setLineDragIndex(typeof lh.idx === 'number' ? lh.idx : null);
          setDragStart(pt);
          setDragMode('moving');
          return;
        }
      }

      // Start new line draft
      setLineDraft([pt]);
      return;
    }

    // Polygon mode: click-to-add vertices; click near first vertex to close.
    if (geometryMode === 'poly') {
      if (polyDraft && polyDraft.length > 0) {
        const first = polyDraft[0];
        if (polyDraft.length >= 3 && dist(pt, first) <= POLY_CLOSE_PX) {
          const stableId = `local_${Date.now()}`;
          const normPts = polyDraft.map((p) => pxToNormPoint(p.x, p.y, w, h));
          const poly = normPts.map((p) => [p[0], p[1]]);
          const bbox = bboxFromPoly(poly);
          setZones((prev) => {
            const next = prev.filter((z) => z.id !== '__temp__' && z.id !== '__temp_poly__');
            return [
              ...next,
              {
                id: stableId,
                zone_id: '',
                name: '',
                zone_type: 'work_area',
                type: 'work_area',
                device_id: deviceId,
                camera_id: cameraId,
                bbox_norm: bbox,
                polygon_norm: poly,
                color: '#00FFFF',
                enabled: true,
                alerts: [],
                max_occupancy: 0,
                restricted: false,
                _local_new: true,
                _auto_zone_id: true,
              } as any,
            ];
          });
          setSelectedZoneId(stableId);
          setPendingNameFocus(true);
          setPolyDraft(null);
          return;
        }

        setPolyDraft([...polyDraft, pt]);
        return;
      }

      // Not drafting: edit existing polygon if hit
      if (hit?.zoneId) {
        setSelectedZoneId(hit.zoneId);
        const z = zones.find((zz) => zz.id === hit.zoneId);
        if (!z) return;
        const poly = (z as any).polygon_norm as number[][] | undefined;
        if (hit.rotate) {
          const ptsPx =
            Array.isArray(poly) && poly.length >= 3
              ? poly.map((p) => normToPxPoint([p[0], p[1]], w, h))
              : rectPolygonFromBBoxNorm(z.bbox_norm || [0, 0, 0, 0], w, h);
          const center = polygonCenter(ptsPx);
          const startAngle = Math.atan2(pt.y - center.y, pt.x - center.x);
          setRotateDragOrigin(ptsPx);
          setRotateCenter(center);
          setRotateStartAngle(startAngle);
          setDragStart(pt);
          setDragMode('rotating');
          return;
        }
        if (Array.isArray(poly) && poly.length >= 3) {
          const ptsPx = poly.map((p) => normToPxPoint([p[0], p[1]], w, h));
          setPolyDragOrigin(ptsPx);
          setPolyDragIndex(typeof hit.polyIndex === 'number' ? hit.polyIndex : null);
          setDragStart(pt);
          setDragMode('moving');
          return;
        }
        if (Array.isArray(z.bbox_norm) && z.bbox_norm.length === 4) {
          const ptsPx = rectPolygonFromBBoxNorm(z.bbox_norm || [0, 0, 0, 0], w, h);
          const normPts = ptsPx.map((p) => pxToNormPoint(p.x, p.y, w, h));
          const nextPoly = normPts.map((p) => [p[0], p[1]]);
          const bbox = bboxFromPoly(nextPoly);
          setZones((prev) =>
            prev.map((zz) =>
              zz.id === z.id
                ? ({ ...zz, polygon_norm: nextPoly as any, bbox_norm: bbox } as any)
                : zz
            )
          );
          setPolyDragOrigin(ptsPx);
          setPolyDragIndex(rectCornerToPolyIndex(hit.corner));
          setDragStart(pt);
          setDragMode('moving');
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Editing selected zone as a polygon in place. Existing zone ID is preserved.',
          });
          return;
        }
      }

      // Start new polygon draft
      setPolyDraft([pt]);
      return;
    }
    if (hit?.zoneId) {
      setSelectedZoneId(hit.zoneId);
      const z = zones.find((zz) => zz.id === hit.zoneId);
      if (!z) return;
      const poly = (z as any).polygon_norm as number[][] | undefined;
      if (hit.rotate) {
        const ptsPx =
          Array.isArray(poly) && poly.length >= 3
            ? poly.map((p) => normToPxPoint([p[0], p[1]], w, h))
            : rectPolygonFromBBoxNorm(z.bbox_norm || [0, 0, 0, 0], w, h);
        const center = polygonCenter(ptsPx);
        const startAngle = Math.atan2(pt.y - center.y, pt.x - center.x);
        setRotateDragOrigin(ptsPx);
        setRotateCenter(center);
        setRotateStartAngle(startAngle);
        setDragStart(pt);
        setDragMode('rotating');
        return;
      }
      if (Array.isArray(poly) && poly.length >= 3) {
        const ptsPx = poly.map((p) => normToPxPoint([p[0], p[1]], w, h));
        setPolyDragOrigin(ptsPx);
        setPolyDragIndex(typeof hit.polyIndex === 'number' ? hit.polyIndex : null);
        setDragStart(pt);
        setDragMode('moving');
        return;
      }
      setDragStart(pt);
      setOrigBBox([...(z.bbox_norm || [0, 0, 0, 0])]);
      if (hit.corner) {
        setDragMode('resizing');
        setActiveCorner(hit.corner);
      } else {
        setDragMode('moving');
        setActiveCorner(null);
      }
      return;
    }

    // Start creating a new zone
    setDragMode('creating');
    setActiveCorner(null);
    setDragStart(pt);
    setOrigBBox(null);
  };

  const onMouseMove = (evt: React.MouseEvent) => {
    if (dragMode === 'idle') return;
    const img = imgRef.current;
    if (!img || !dragStart) return;

    const w = img.clientWidth;
    const h = img.clientHeight;
    const pt = getMouse(evt);

    if (dragMode === 'rotating' && selectedZone && rotateDragOrigin && rotateCenter && rotateStartAngle !== null) {
      const angle = Math.atan2(pt.y - rotateCenter.y, pt.x - rotateCenter.x);
      const rawDelta = angle - rotateStartAngle;
      const shouldSnap = evt.shiftKey && rotationSnapStepDeg > 0;
      const delta = shouldSnap ? snapAngleDelta(rawDelta, rotationSnapStepDeg) : rawDelta;
      const rotatedPx = rotateDragOrigin.map((p) => {
        const rp = rotatePointAround(p, rotateCenter, delta);
        return { x: clamp(rp.x, 0, w), y: clamp(rp.y, 0, h) };
      });
      const normPts = rotatedPx.map((p) => pxToNormPoint(p.x, p.y, w, h));
      const poly = normPts.map((p) => [p[0], p[1]]);
      const bbox = bboxFromPoly(poly);
      setZones((prev) =>
        prev.map((z) =>
          z.id === selectedZone.id
            ? ({ ...z, polygon_norm: poly, bbox_norm: bbox } as any)
            : z
        )
      );
      return;
    }

    // Line editing (endpoint drag / move)
    if (geometryMode === 'line' && selectedLine && lineDragOrigin) {
      const dx = pt.x - dragStart.x;
      const dy = pt.y - dragStart.y;
      const movedPx = lineDragOrigin.map((p, idx) => {
        if (lineDragIndex !== null) {
          if (idx === lineDragIndex) return { x: clamp(p.x + dx, 0, w), y: clamp(p.y + dy, 0, h) };
          return p;
        }
        // move whole line
        return { x: clamp(p.x + dx, 0, w), y: clamp(p.y + dy, 0, h) };
      });
      const a = pxToNormPoint(movedPx[0].x, movedPx[0].y, w, h);
      const b = pxToNormPoint(movedPx[1].x, movedPx[1].y, w, h);
      setLines((prev) =>
        prev.map((l) =>
          l.id === selectedLine.id
            ? ({ ...l, points_norm: [[a[0], a[1]], [b[0], b[1]]] } as any)
            : l
        )
      );
      return;
    }

    // Polygon editing (vertex drag / move)
    if (
      selectedZone &&
      polyDragOrigin &&
      (geometryMode === 'poly' ||
        (Array.isArray((selectedZone as any).polygon_norm) && (selectedZone as any).polygon_norm.length >= 3))
    ) {
      const dx = pt.x - dragStart.x;
      const dy = pt.y - dragStart.y;
      const movedPx = polyDragOrigin.map((p, idx) => {
        if (polyDragIndex !== null) {
          if (idx === polyDragIndex) return { x: clamp(p.x + dx, 0, w), y: clamp(p.y + dy, 0, h) };
          return p;
        }
        // move whole polygon
        return { x: clamp(p.x + dx, 0, w), y: clamp(p.y + dy, 0, h) };
      });
      const normPts = movedPx.map((p) => pxToNormPoint(p.x, p.y, w, h));
      const poly = normPts.map((p) => [p[0], p[1]]);
      const bbox = bboxFromPoly(poly);
      setZones((prev) =>
        prev.map((z) => (z.id === selectedZone.id ? ({ ...z, polygon_norm: poly, bbox_norm: bbox } as any) : z))
      );
      return;
    }

    if (dragMode === 'creating') {
      // show a temporary rectangle by mutating (or creating) a temporary zone
      const x1 = clamp(Math.min(dragStart.x, pt.x), 0, w);
      const y1 = clamp(Math.min(dragStart.y, pt.y), 0, h);
      const x2 = clamp(Math.max(dragStart.x, pt.x), 0, w);
      const y2 = clamp(Math.max(dragStart.y, pt.y), 0, h);
      const bw = Math.max(1, x2 - x1);
      const bh = Math.max(1, y2 - y1);

      const tempId = '__temp__';
      const bbox = pxToNorm(x1, y1, bw, bh, w, h);
      setZones((prev) => {
        const existing = prev.find((z) => z.id === tempId);
        const base: LocalZone =
          existing ||
          ({
            id: tempId,
            zone_id: `draft_${Date.now()}`,
            name: 'New Zone',
            zone_type: 'work_area',
            type: 'work_area',
            device_id: deviceId,
            camera_id: cameraId,
            bbox_norm: bbox,
            color: '#00FFFF',
            enabled: true,
            alerts: [],
            max_occupancy: 0,
            restricted: false,
            _local_new: true,
            _auto_zone_id: true,
          } as any);
        const next = prev.filter((z) => z.id !== tempId);
        return [...next, { ...base, bbox_norm: bbox }];
      });
      setSelectedZoneId('__temp__');
      return;
    }

    if (dragMode === 'moving' && origBBox && selectedZone) {
      const [nx, ny, nw, nh] = origBBox;
      const dx = (pt.x - dragStart.x) / w;
      const dy = (pt.y - dragStart.y) / h;
      const nx2 = clamp(nx + dx, 0, 1 - nw);
      const ny2 = clamp(ny + dy, 0, 1 - nh);
      const bbox = [nx2, ny2, nw, nh];
      setZones((prev) => prev.map((z) => (z.id === selectedZone.id ? { ...z, bbox_norm: bbox } : z)));
      return;
    }

    if (dragMode === 'resizing' && origBBox && selectedZone && activeCorner) {
      const [nx, ny, nw, nh] = origBBox;
      const x1 = nx * w;
      const y1 = ny * h;
      const x2 = (nx + nw) * w;
      const y2 = (ny + nh) * h;

      let rx1 = x1;
      let ry1 = y1;
      let rx2 = x2;
      let ry2 = y2;

      if (activeCorner === 'nw' || activeCorner === 'sw') rx1 = clamp(pt.x, 0, rx2 - 2);
      if (activeCorner === 'ne' || activeCorner === 'se') rx2 = clamp(pt.x, rx1 + 2, w);
      if (activeCorner === 'nw' || activeCorner === 'ne') ry1 = clamp(pt.y, 0, ry2 - 2);
      if (activeCorner === 'sw' || activeCorner === 'se') ry2 = clamp(pt.y, ry1 + 2, h);

      const bbox = pxToNorm(rx1, ry1, rx2 - rx1, ry2 - ry1, w, h);
      setZones((prev) => prev.map((z) => (z.id === selectedZone.id ? { ...z, bbox_norm: bbox } : z)));
    }
  };

  const onMouseUp = () => {
    if (polyDragOrigin) {
      setPolyDragIndex(null);
      setPolyDragOrigin(null);
    }
    if (lineDragOrigin) {
      setLineDragIndex(null);
      setLineDragOrigin(null);
    }
    setRotateDragOrigin(null);
    setRotateCenter(null);
    setRotateStartAngle(null);
    if (dragMode === 'creating') {
      // Finalize temp zone using the *latest* state (avoid stale closure causing "draw then disappear").
      const stableId = `local_${Date.now()}`;
      setZones((prev) => {
        const temp = prev.find((z) => z.id === '__temp__');
        if (!temp) return prev;

        // Use normalized size threshold (independent of img/clientWidth).
        const bb = temp.bbox_norm || [0, 0, 0, 0];
        const nw = Number(bb[2] || 0);
        const nh = Number(bb[3] || 0);
        if (!Number.isFinite(nw) || !Number.isFinite(nh) || nw < MIN_DRAW_NORM || nh < MIN_DRAW_NORM) {
          return prev.filter((z) => z.id !== '__temp__');
        }

        return prev.map((z) =>
          z.id === '__temp__'
            ? {
                ...z,
                id: stableId,
                _local_new: true,
                _auto_zone_id: true,
                // Encourage draw-then-label flow: focus name field after draw.
                name: z.name === 'New Zone' ? '' : z.name,
                zone_id: z.zone_id && z.zone_id.startsWith('draft_') ? '' : z.zone_id,
              }
            : z
        );
      });
      setSelectedZoneId(stableId);
      setPendingNameFocus(true);
    }

    setDragMode('idle');
    setActiveCorner(null);
    setDragStart(null);
    setOrigBBox(null);
  };

  const updateSelected = useCallback((patch: Partial<LocalZone>) => {
    if (!selectedZone) return;
    if (
      ('bbox_norm' in patch || 'polygon_norm' in patch) &&
      !ensureGeometryAuthoringReady('Geometry editing')
    ) {
      return;
    }
    setZones((prev) => prev.map((z) => (z.id === selectedZone.id ? { ...z, ...patch } : z)));
  }, [ensureGeometryAuthoringReady, selectedZone]);

  const selectedZoneIsPolygon = Boolean(
    selectedZone &&
      Array.isArray((selectedZone as any).polygon_norm) &&
      (selectedZone as any).polygon_norm.length >= 3
  );

  const rotateSelectedZoneByDeg = useCallback(
    (deltaDeg: number) => {
      if (!selectedZone) return;
      if (!ensureGeometryAuthoringReady('Zone rotation')) return;
      const img = imgRef.current;
      if (!img) return;
      const w = img.clientWidth || img.naturalWidth || 1;
      const h = img.clientHeight || img.naturalHeight || 1;
      const poly = (selectedZone as any).polygon_norm as number[][] | undefined;
      const ptsPx =
        Array.isArray(poly) && poly.length >= 3
          ? poly.map((p) => normToPxPoint([p[0], p[1]], w, h))
          : rectPolygonFromBBoxNorm(selectedZone.bbox_norm || [0, 0, 0, 0], w, h);
      const center = polygonCenter(ptsPx);
      const deltaRad = (deltaDeg * Math.PI) / 180;
      const rotatedPx = ptsPx.map((p) => rotatePointAround(p, center, deltaRad));
      const normPts = rotatedPx.map((p) => pxToNormPoint(clamp(p.x, 0, w), clamp(p.y, 0, h), w, h));
      const nextPoly = normPts.map((p) => [p[0], p[1]]);
      const bbox = bboxFromPoly(nextPoly);
      updateSelected({ polygon_norm: nextPoly as any, bbox_norm: bbox });
      setGeometryMode('poly');
    },
    [ensureGeometryAuthoringReady, selectedZone, updateSelected]
  );

  const convertSelectedZoneToPolygon = useCallback(() => {
    if (!selectedZone) return;
    if (!ensureGeometryAuthoringReady('Polygon conversion')) return;
    const img = imgRef.current;
    if (!img) return;
    const w = img.clientWidth || img.naturalWidth || 1;
    const h = img.clientHeight || img.naturalHeight || 1;
    const ptsPx = rectPolygonFromBBoxNorm(selectedZone.bbox_norm || [0, 0, 0, 0], w, h);
    const normPts = ptsPx.map((p) => pxToNormPoint(p.x, p.y, w, h));
    const nextPoly = normPts.map((p) => [p[0], p[1]]);
    updateSelected({ polygon_norm: nextPoly as any, bbox_norm: bboxFromPoly(nextPoly) });
    setGeometryMode('poly');
    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Zone converted to polygon. Drag white vertices to edit shape.',
    });
  }, [ensureGeometryAuthoringReady, selectedZone, updateSelected]);

  const resetSelectedZoneToRectangle = useCallback(() => {
    if (!selectedZone) return;
    if (!ensureGeometryAuthoringReady('Rectangle reset')) return;
    const poly = (selectedZone as any).polygon_norm as number[][] | undefined;
    const bbox =
      Array.isArray(poly) && poly.length >= 3 ? bboxFromPoly(poly) : selectedZone.bbox_norm || [0, 0, 0, 0];
    updateSelected({ polygon_norm: undefined as any, bbox_norm: bbox });
    setGeometryMode('rect');
    setSnackbar({
      open: true,
      severity: 'success',
      message: 'Zone reset to rectangle geometry.',
    });
  }, [ensureGeometryAuthoringReady, selectedZone, updateSelected]);

  const updateSelectedLine = (patch: Partial<LocalLine>) => {
    if (!selectedLine) return;
    if ('points_norm' in patch && !ensureGeometryAuthoringReady('Line editing')) return;
    setLines((prev) => prev.map((l) => (l.id === selectedLine.id ? ({ ...l, ...patch } as any) : l)));
  };

  const saveDraft = async () => {
    if (!deviceId || !cameraId) return;
    setZonesLoading(true);
    setLinesLoading(true);
    try {
      // F-09: Auto-generate stable IDs from name when missing (name stays editable; IDs remain stable once set).
      const _slug = (s: string) =>
        String(s || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 48) || 'unnamed';
      const _suggestId = (prefix: 'zone' | 'line', name: string, used: Set<string>) => {
        const base = `${prefix}_${_slug(name)}`;
        let cand = base;
        let i = 2;
        while (used.has(cand)) cand = `${base}_${i++}`;
        used.add(cand);
        return cand;
      };

      const zoneIdsUsed = new Set<string>((zones || []).map((z: any) => String(z?.zone_id || '').trim()).filter(Boolean));
      const lineIdsUsed = new Set<string>((lines || []).map((l: any) => String(l?.line_id || '').trim()).filter(Boolean));
      const generatedZoneIds: Record<string, string> = {};
      const generatedLineIds: Record<string, string> = {};

      for (const z of zones as any[]) {
        const nm = String(z?.name || '').trim();
        const zid = String(z?.zone_id || '').trim();
        if (!zid && nm) {
          const gen = _suggestId('zone', nm, zoneIdsUsed);
          generatedZoneIds[String(z?.id)] = gen;
        }
      }
      for (const l of lines as any[]) {
        const nm = String(l?.name || '').trim();
        const lid = String(l?.line_id || '').trim();
        if (!lid && nm) {
          const gen = _suggestId('line', nm, lineIdsUsed);
          generatedLineIds[String(l?.id)] = gen;
        }
      }

      if (Object.keys(generatedZoneIds).length > 0) {
        setZones((prev: any[]) =>
          (prev || []).map((z: any) =>
            generatedZoneIds[String(z?.id)] ? { ...z, zone_id: generatedZoneIds[String(z?.id)] } : z
          )
        );
      }
      if (Object.keys(generatedLineIds).length > 0) {
        setLines((prev: any[]) =>
          (prev || []).map((l: any) =>
            generatedLineIds[String(l?.id)] ? { ...l, line_id: generatedLineIds[String(l?.id)] } : l
          )
        );
      }

      const zonesForSave = (zones || []).map((z: any) => ({
        ...z,
        zone_id: String(generatedZoneIds[String(z?.id)] || z?.zone_id || '').trim(),
      })) as Array<any>;
      const linesForSave = (lines || []).map((l: any) => ({
        ...l,
        line_id: String(generatedLineIds[String(l?.id)] || l?.line_id || '').trim(),
      })) as Array<any>;

      // Basic UI-side guardrail: ensure zone_id uniqueness (within this camera) for zones that are save-eligible.
      const zoneIdCounts = new Map<string, number>();
      for (const z of zonesForSave) {
        const zid = String((z as any).zone_id || '').trim();
        const nm = String((z as any).name || '').trim();
        if (!zid || !nm) continue;
        zoneIdCounts.set(zid, (zoneIdCounts.get(zid) || 0) + 1);
      }
      const dupZoneIds = Array.from(zoneIdCounts.entries())
        .filter(([, c]) => c > 1)
        .map(([k]) => k);
      if (dupZoneIds.length > 0) {
        window.alert(`Duplicate Zone ID(s) for this camera:\n\n- ${dupZoneIds.join('\n- ')}\n\nFix before saving.`);
        return;
      }

      // Guardrail: ensure line_id uniqueness (within this camera) for lines that are save-eligible.
      const lineIdCounts = new Map<string, number>();
      for (const l of linesForSave) {
        const lid = String((l as any).line_id || '').trim();
        const nm = String((l as any).name || '').trim();
        if (!lid || !nm) continue;
        lineIdCounts.set(lid, (lineIdCounts.get(lid) || 0) + 1);
      }
      const dupLineIds = Array.from(lineIdCounts.entries())
        .filter(([, c]) => c > 1)
        .map(([k]) => k);
      if (dupLineIds.length > 0) {
        window.alert(`Duplicate Line ID(s) for this camera:\n\n- ${dupLineIds.join('\n- ')}\n\nFix before saving.`);
        return;
      }

      const isLocalZone = (z: any) => Boolean(z?._local_new) || String(z?.id || '').startsWith('local_');
      const isLocalLine = (l: any) => Boolean(l?._local_new) || String(l?.id || '').startsWith('local_line_');
      let zonesSaved = 0;
      let linesSaved = 0;
      const zoneSaveErrors: string[] = [];
      let lineSaveFailures = 0;

      // Persist all zones currently in the editor
      for (const z of zonesForSave) {
        const zid = String((z as any).zone_id || '').trim();
        const local = isLocalZone(z);
        if (local && (!z.name || !zid)) {
          continue;
        }

        const rawBbox = Array.isArray(z.bbox_norm) ? z.bbox_norm.slice(0, 4).map((n: any) => Number(n)) : [];
        const bbox =
          rawBbox.length >= 4 && rawBbox.every((n: number) => Number.isFinite(n))
            ? rawBbox
            : null;
        const poly = Array.isArray((z as any).polygon_norm) && (z as any).polygon_norm.length >= 3
          ? (z as any).polygon_norm
              .filter((pt: any) => Array.isArray(pt) && pt.length >= 2)
              .map((pt: any) => [Number(pt[0]), Number(pt[1])])
          : null;
        if (!bbox && !poly) {
          zoneSaveErrors.push(`${zid || z.name}: bbox_norm or polygon_norm required`);
          continue;
        }
        const payload: any = {
          zone_id: zid,
          name: z.name,
          device_id: deviceId,
          camera_id: cameraId,
          bbox_norm: bbox,
          polygon_norm: poly || undefined,
          color: z.color,
          zone_type: z.zone_type || z.type,
          restricted: z.restricted,
          severity: z.severity,
          max_occupancy: z.max_occupancy,
          alerts: z.alerts || [],
          enabled: z.enabled !== false,
        };

        try {
          if (local) {
            // Local-first flow: create, and if it already exists then update by stable zone_id.
            let result: any = null;
            try {
              result = await zonesApi.createZone(payload);
            } catch (err: any) {
              if (err?.response?.status === 409) {
                result = await zonesApi.updateZone(payload.zone_id, payload);
              } else {
                throw err;
              }
            }
            zonesSaved += 1;
            const nextId = String(result?.id || z.id || '');
            const nextZoneId = String(result?.zone_id || payload.zone_id || z.zone_id || '');
            setZones((prev) =>
              (prev || []).map((x: any) =>
                String(x?.id || '') === String(z?.id || '')
                  ? { ...x, id: nextId || x.id, zone_id: nextZoneId || x.zone_id, _local_new: false }
                  : x
              )
            );
            if (nextId) {
              setSelectedZoneId((prevSel) =>
                String(prevSel || '') === String(z?.id || '') ? nextId : prevSel
              );
            }
          } else {
            await zonesApi.updateZone(z.id, payload);
            zonesSaved += 1;
          }
        } catch (error: any) {
          const label = String(zid || z.name || z.id || 'zone');
          const errData = error?.response?.data?.error;
          const errMsg =
            typeof errData === 'object' && errData?.message
              ? errData.message
              : typeof errData === 'string'
                ? errData
                : error?.response?.data?.error || error?.message || String(error);
          zoneSaveErrors.push(`${label}: ${typeof errMsg === 'object' ? JSON.stringify(errMsg) : errMsg}`);
          console.error(`❌ Failed to save zone ${label}:`, error);
        }
      }

      // Persist all lines currently in the editor
      const existingDbLineIds = new Set<string>(
        (linesForSave || [])
          .filter((l: any) => !isLocalLine(l))
          .map((l: any) => String(l?.line_id || '').trim())
          .filter(Boolean)
      );

      const isFiniteNumber = (v: any) => typeof v === 'number' && Number.isFinite(v);
      const isValidNormPoint = (p: any) =>
        Array.isArray(p) &&
        p.length === 2 &&
        isFiniteNumber(p[0]) &&
        isFiniteNumber(p[1]) &&
        p[0] >= 0 &&
        p[0] <= 1 &&
        p[1] >= 0 &&
        p[1] <= 1;

      for (const l of linesForSave) {
        // Skip lines that are missing required fields
        const lid = String((l as any).line_id || '').trim();
        if ((l._local_new || (l.id || '').startsWith('local_line_')) && (!l.name || !lid)) {
          console.warn(`Skipping line save: missing name or line_id`, { name: l.name, line_id: lid, id: l.id });
          continue;
        }

        // Validate points_norm (must be exactly 2 points)
        const pts = l.points_norm || [];
        if (!Array.isArray(pts) || pts.length !== 2) {
          console.warn(`Skipping line save: invalid points_norm (must be 2 points)`, {
            name: l.name,
            line_id: l.line_id,
            points_norm: pts,
            points_count: Array.isArray(pts) ? pts.length : 'not array'
          });
          continue;
        }
        if (!isValidNormPoint(pts[0]) || !isValidNormPoint(pts[1])) {
          console.warn(`Skipping line save: invalid normalized points (must be finite numbers in [0,1])`, {
            name: l.name,
            line_id: l.line_id,
            points_norm: pts,
          });
          continue;
        }

        const payload: any = {
          line_id: lid,
          name: l.name,
          device_id: deviceId,
          camera_id: cameraId,
          points_norm: pts,
          color: l.color || '#00FFFF',
          thickness_px: (l as any).thickness_px || 6,
          enabled: l.enabled !== false,
        };

        try {
          const local = isLocalLine(l as any);
          if (local) {
            // If a line with this line_id already exists in DB (e.g. user redrew or refreshed weirdly),
            // treat this as an UPDATE rather than a CREATE to avoid 409.
            if (existingDbLineIds.has(payload.line_id)) {
              const result = await linesApi.updateLine(payload.line_id, payload);
              console.log(`✅ Updated line (collision): ${payload.name} (line_id: ${payload.line_id})`, result);
              linesSaved += 1;
              setLines((prev) =>
                (prev || []).map((x: any) =>
                  x?.id === (l as any).id ? { ...x, id: result?.id || x.id, _local_new: false } : x
                )
              );
            } else {
              try {
                const result = await linesApi.createLine(payload);
                console.log(`✅ Created line: ${payload.name} (line_id: ${payload.line_id})`, result);
                existingDbLineIds.add(payload.line_id);
                linesSaved += 1;
                setLines((prev) =>
                  (prev || []).map((x: any) =>
                    x?.id === (l as any).id ? { ...x, id: result?.id || x.id, _local_new: false } : x
                  )
                );
              } catch (err: any) {
                // If we lost a race or line already exists, fall back to update.
                const status = err?.response?.status;
                if (status === 409) {
                  const result = await linesApi.updateLine(payload.line_id, payload);
                  console.log(`✅ Updated line (after 409): ${payload.name} (line_id: ${payload.line_id})`, result);
                  existingDbLineIds.add(payload.line_id);
                  linesSaved += 1;
                  setLines((prev) =>
                    (prev || []).map((x: any) =>
                      x?.id === (l as any).id ? { ...x, id: result?.id || x.id, _local_new: false } : x
                    )
                  );
                } else {
                  throw err;
                }
              }
            }
          } else {
            const result = await linesApi.updateLine(l.id, payload);
            console.log(`✅ Updated line: ${payload.name} (line_id: ${payload.line_id})`, result);
            linesSaved += 1;
          }
        } catch (error: any) {
          console.error(`❌ Failed to save line ${payload.name}:`, error);
          lineSaveFailures += 1;
          // Continue with other lines even if one fails
        }
      }

      // Post-save refresh should be authoritative: drop stale local copies.
      await refreshZones({ preserveLocal: false });
      await refreshLines({ preserveLocal: false });
      hasUnsavedEditsRef.current = false;

      if (zoneSaveErrors.length > 0 || lineSaveFailures > 0) {
        setDraftSaveState('error');
        const problems: string[] = [];
        if (zoneSaveErrors.length > 0) {
          problems.push(`zones failed: ${zoneSaveErrors.length}`);
          if (zoneSaveErrors[0]) problems.push(zoneSaveErrors[0]);
        }
        if (lineSaveFailures > 0) {
          problems.push(`lines failed: ${lineSaveFailures}`);
        }
        setSnackbar({
          open: true,
          message: `Saved with errors: ${problems.join(' — ')}`,
          severity: 'error',
        });
      } else {
        setDraftSaveState('saved');
        setLastDraftSavedAtMs(Date.now());
        setSnackbar({
          open: true,
          message: `Draft saved (${zonesSaved} zones, ${linesSaved} lines).`,
          severity: 'success',
        });
      }
    } catch (error: any) {
      console.error('Failed to save draft:', error);
      setDraftSaveState('error');
      setSnackbar({
        open: true,
        message: `Save draft failed: ${error?.message || String(error)}`,
        severity: 'error',
      });
    } finally {
      setZonesLoading(false);
      setLinesLoading(false);
    }
  };
  const deleteSelected = async () => {
    if (!selectedZone) return;
    const label = selectedZone.name || selectedZone.zone_id || selectedZone.id;

    // Unsaved/local zones: just drop from UI state.
    if ((selectedZone as any)?._local_new || String(selectedZone.id || '').startsWith('local_')) {
      setZones((prev) => prev.filter((z) => z.id !== selectedZone.id));
      setSelectedZoneId('');
      return;
    }

    const ok = window.confirm(
      `Delete zone "${label}"?\n\nThis removes it from the cloud (soft delete).\nTo remove it from the edge pipeline you must click Validate + Deploy after deleting.`
    );
    if (!ok) return;

    setZonesLoading(true);
    try {
      const scope = ensureUiScope();
      const orgId = String(scope.orgId || '').trim() || 'default';
      const resolvedSiteId = String((siteId || scope.siteId || '').trim());
      if (!resolvedSiteId) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Site scope is missing. Set org/site in the header, then retry delete.',
        });
        return;
      }
      await zonesApi.deleteZone(selectedZone.id, orgId, resolvedSiteId);
      await refreshZones({ preserveLocal: false });
      setSelectedZoneId('');
      setSnackbar({ open: true, severity: 'success', message: `Zone "${label}" deleted.` });
    } catch (e: any) {
      const status = e?.response?.status;
      const d = e?.response?.data;
      const detail = typeof d?.detail === 'string' ? d.detail : '';
      const cap = typeof d?.capability === 'string' ? d.capability : '';
      let msg =
        typeof d?.error === 'string'
          ? d.error
          : typeof d?.message === 'string'
            ? d.message
            : e?.message || 'Delete failed';
      if (status === 403 && detail === 'insufficient_capability' && cap) {
        msg = `Delete forbidden: missing ${cap}. Your role may still be viewer-equivalent on the server (e.g. pilot_site_admin without migration 80 / policies:write).`;
      } else if (status === 403) {
        msg = `Delete forbidden (HTTP 403). ${detail || msg}`;
      } else if (status === 404) {
        msg = 'Zone not found for this org/site (check scope matches where the zone was created).';
      }
      console.error('Failed to delete zone', e);
      setSnackbar({ open: true, severity: 'error', message: String(msg) });
    } finally {
      setZonesLoading(false);
    }
  };

  const openSaveTemplateDialog = () => {
    if (!selectedZone) return;
    const nm = (selectedZone.name || selectedZone.zone_id || 'Zone Template').toString();
    setSaveTemplateName(nm);
    setSaveTemplateDescription('');
    setSaveTemplateOpen(true);
  };

  const saveSelectedZoneAsTemplate = async () => {
    if (!selectedZone) return;
    const nm = (saveTemplateName || '').trim();
    if (!nm) {
      setSnackbar({ open: true, message: 'Template name is required.', severity: 'error' });
      return;
    }
    setZoneTemplatesLoading(true);
    try {
      await zoneTemplatesApi.create(siteId, {
        name: nm,
        description: (saveTemplateDescription || '').trim() || undefined,
        zone_type: (selectedZone.zone_type || selectedZone.type || undefined) as any,
        color: selectedZone.color || undefined,
        bbox_norm: (selectedZone.bbox_norm as any) || null,
        polygon_norm: ((selectedZone as any).polygon_norm as any) || null,
        defaults: {
          restricted: Boolean((selectedZone as any).restricted),
          notification_cadence: (selectedZone as any).notification_cadence,
        },
      });
      setSaveTemplateOpen(false);
      setSnackbar({ open: true, message: 'Template saved.', severity: 'success' });
      await refreshZoneTemplates();
    } catch (e) {
      console.error('Failed to save template:', e);
      setSnackbar({ open: true, message: 'Failed to save template.', severity: 'error' });
    } finally {
      setZoneTemplatesLoading(false);
    }
  };

  const applyTemplateToCurrentCamera = () => {
    const tid = (selectedTemplateId || '').trim();
    if (!tid) return;
    const t = zoneTemplates.find((x) => x.template_id === tid);
    if (!t) return;
    const stableId = `local_${Date.now()}`;
    const baseName = (t.name || 'Template').toString();
    const zid = `${slugify(baseName)}_${Date.now().toString().slice(-4)}`;
    const poly = (t.polygon_norm as any) || null;
    const bbox = (t.bbox_norm as any) || (Array.isArray(poly) && poly.length >= 3 ? bboxFromPoly(poly as any) : [0, 0, 0.2, 0.2]);
    const z: any = {
      id: stableId,
      zone_id: zid,
      name: baseName,
      zone_type: t.zone_type || 'work_area',
      type: t.zone_type || 'work_area',
      device_id: deviceId,
      camera_id: cameraId,
      bbox_norm: bbox,
      polygon_norm: poly,
      color: t.color || '#00FFFF',
      enabled: true,
      alerts: [],
      max_occupancy: 0,
      restricted: Boolean((t.defaults as any)?.restricted),
      notification_cadence: (t.defaults as any)?.notification_cadence,
      _local_new: true,
      _auto_zone_id: true,
    };
    setZones((prev) => [...(prev || []), z]);
    setSelectedZoneId(stableId);
    setSelectedLineId('');
    setPendingNameFocus(true);
  };

  const deleteSelectedLine = async () => {
    if (!selectedLine) return;
    const label = selectedLine.name || selectedLine.line_id || selectedLine.id;

    if ((selectedLine as any)?._local_new || String(selectedLine.id || '').startsWith('local_line_')) {
      setLines((prev) => prev.filter((l) => l.id !== selectedLine.id));
      setSelectedLineId('');
      return;
    }

    const ok = window.confirm(
      `Delete line "${label}"?\n\nThis removes it from the cloud (soft delete).`
    );
    if (!ok) return;

    setLinesLoading(true);
    try {
      await linesApi.deleteLine(selectedLine.id);
      await refreshLines();
      setSelectedLineId('');
    } finally {
      setLinesLoading(false);
    }
  };

  const _validateInternal = async () => {
    // Treat "no_enabled_*" as non-fatal for a combined Zones+Lines editor:
    // users should be able to deploy zones even if they haven't created any lines yet (and vice versa).
    const makeEmptyOk = (configType: 'zones' | 'lines') => ({
      ok: true,
      config_type: configType,
      summary: { errors: 0, warnings: 0 },
      count: 0,
      empty: true,
    });

    let outZones: any;
    let outLines: any;

    try {
      outZones = await zonesApi.validateZones(siteId, { edge_device_id: deviceId, camera_id: cameraId });
    } catch (e: any) {
      const status = e?.response?.status;
      const errCode = e?.response?.data?.error;
      if (status === 400 && errCode === 'no_enabled_zones') {
        outZones = makeEmptyOk('zones');
      } else {
        throw e;
      }
    }

    try {
      outLines = await linesApi.validateLines(siteId, { edge_device_id: deviceId, camera_id: cameraId });
    } catch (e: any) {
      const status = e?.response?.status;
      const errCode = e?.response?.data?.error;
      if (status === 400 && errCode === 'no_enabled_lines') {
        outLines = makeEmptyOk('lines');
      } else {
        throw e;
      }
    }
    const out = {
      ok: Boolean(outZones?.ok) && Boolean(outLines?.ok),
      zones: outZones,
      lines: outLines,
      summary: {
        errors: Number(outZones?.summary?.errors || 0) + Number(outLines?.summary?.errors || 0),
        warnings: Number(outZones?.summary?.warnings || 0) + Number(outLines?.summary?.warnings || 0),
      },
    };
    setValidation(out);
    return out;
  };

  // Reserved for future Validate button
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validate = async () => {
    if (!cameraId) return null;
    try {
      setAction('validating');
      const out = await _validateInternal();
      if (out?.ok === true) {
        setSnackbar({ open: true, message: 'Validation OK.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Validation failed. Fix issues before deploy.', severity: 'error' });
      }
      return out;
    } catch (e: any) {
      setSnackbar({ open: true, message: `Validation error: ${e?.message || String(e)}`, severity: 'error' });
      return null;
    } finally {
      setAction('idle');
    }
  };

  const deploy = async () => {
    if (!cameraId) return;
    try {
      setAction('deploying');
      const hasUnsavedZones = (zones || []).some((z: any) => Boolean(z?._local_new) || String(z?.id || '').startsWith('local_'));
      const hasUnsavedLines = (lines || []).some((l: any) => Boolean(l?._local_new) || String(l?.id || '').startsWith('local_line_'));
      const hasUnsaved = hasUnsavedEditsRef.current || hasUnsavedZones || hasUnsavedLines;
      if (hasUnsaved) {
        setSnackbar({
          open: true,
          message: 'You have unsaved edits. Click Save Draft first, then Validate + Deploy.',
          severity: 'error',
        });
        return;
      }
      const v = await _validateInternal();
      if (!v || v.ok !== true) {
        setSnackbar({ open: true, message: 'Deploy blocked: validation failed.', severity: 'error' });
        return;
      }

      // Gate 4: deploy is fail-closed when monitoring is not armed.
      // "Validate + Deploy" should be the UX path to safely go-live, so we record a validation pass
      // and arm the device before publishing bundles.
      const orgId = String(v?.zones?.org_id || v?.lines?.org_id || 'default');
      try {
        await api.post(`/api/v1/sites/${siteId}/validation/runs`, {
          org_id: orgId,
          device_id: deviceId,
          status: 'pass',
          details: {
            source: 'zone_snapshot_editor',
            zones_summary: v?.zones?.summary || null,
            lines_summary: v?.lines?.summary || null,
          },
        });
      } catch {
        // Best-effort: arming may still succeed if another validation pass exists.
      }
      try {
        await api.post(`/api/v1/sites/${siteId}/arming`, {
          org_id: orgId,
          device_id: deviceId,
          monitoring_enabled: true,
          require_validation_pass: true,
        });
      } catch (e: any) {
        const status = e?.response?.status;
        const errCode = e?.response?.data?.error;
        if (status === 409 && errCode) {
          setSnackbar({
            open: true,
            message: `Deploy blocked: could not arm monitoring (${errCode}).`,
            severity: 'error',
          });
          return;
        }
        throw e;
      }

      // Deploy zones + lines. If either side has "no_enabled_*", treat as a no-op
      // so users can still deploy the other configuration type.
      let dz: any = null;
      let dl: any = null;
      try {
        dz = await zonesApi.deployZones(siteId, { edge_device_id: deviceId, camera_ids: [cameraId] });
      } catch (e: any) {
        const status = e?.response?.status;
        const errCode = e?.response?.data?.error;
        if (!(status === 400 && errCode === 'no_enabled_zones')) throw e;
      }
      try {
        dl = await linesApi.deployLines(siteId, { edge_device_id: deviceId, camera_ids: [cameraId] });
      } catch (e: any) {
        const status = e?.response?.status;
        const errCode = e?.response?.data?.error;
        if (!(status === 400 && errCode === 'no_enabled_lines')) throw e;
      }

      const depId = dz?.deployment_id || dl?.deployment_id;
      setSnackbar({
        open: true,
        message: depId ? `Deployed (deployment ${depId}).` : 'Deployed.',
        severity: 'success',
      });
      // Refresh editor state after deploy
      await refreshZones();
      await refreshLines();
    } catch (e: any) {
      const status = e?.response?.status;
      const errCode = e?.response?.data?.error;
      const detail = e?.response?.data?.detail;
      const msg =
        status && errCode
          ? `Deploy error (${status}): ${errCode}${detail ? ` — ${String(detail).slice(0, 200)}` : ''}`
          : `Deploy error: ${e?.message || String(e)}`;
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setAction('idle');
    }
  };

  const formatAge = (ms: number) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
  };

  const confirmDiscardUnsavedWork = useCallback((): boolean => {
    if (!hasUnsavedEditsRef.current) return true;
    return window.confirm(
      'Switch camera and discard unsaved zone/line edits for the current camera?'
    );
  }, []);

  const header = (
    <Box display="flex" gap={2} alignItems="center">
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel>Device</InputLabel>
        <Select
          label="Device"
          value={deviceId}
          onChange={(e) => {
            if (!confirmDiscardUnsavedWork()) return;
            if (!confirmUnlockAndClearEdits()) return;
            const next = String(e.target.value || '');
            setDeviceId(next);
            const firstCam = cameras.find((c) => c.edge_device_id === next);
            setCameraId(firstCam?.camera_id || '');
            setEdgeCamerasForDevice([]);
          }}
        >
          {deviceIds.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel>Camera</InputLabel>
        <Select
          label="Camera"
          value={cameraId}
          onChange={(e) => {
            if (!confirmDiscardUnsavedWork()) return;
            if (!confirmUnlockAndClearEdits()) return;
            setCameraId(String(e.target.value || ''));
          }}
          displayEmpty
        >
          {camerasForDevice.length === 0 ? (
            <MenuItem value="" disabled>
              {deviceId ? 'Loading… or run Preflight to claim cameras' : 'Select device first'}
            </MenuItem>
          ) : (
            camerasForDevice.map((c) => (
              <MenuItem key={c.camera_id} value={c.camera_id}>
                {c.camera_id}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      {/* F-09: Show lock indicator when camera is locked */}
      {cameraLocked && (
        <Tooltip title="Locked because you have an active selection/draft. Click to unlock (clears selection/draft).">
          <Chip
            label="Locked (click to unlock)"
            color="warning"
            size="small"
            icon={<LockIcon />}
            clickable
            onClick={() => {
              confirmUnlockAndClearEdits();
            }}
          />
        </Tooltip>
      )}

      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={() => void refreshSnapshot({ userInitiated: true })}
        disabled={snapshotLoading || !cameraId || zonesLoading || linesLoading || actionBusy || cameraLocked}
      >
        Refresh Snapshot
      </Button>
      {snapshotFetchedAtMs && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>Last snapshot:</strong> {formatAge(Date.now() - snapshotFetchedAtMs)}
          </Typography>
          {snapshotSource === 'mjpeg' ? (
            <Chip label="Edge live framing" color="info" size="small" variant="outlined" />
          ) : snapshotSource === 'forced' ? (
            <Chip label="Fallback snapshot" color="warning" size="small" variant="outlined" />
          ) : null}
          {Date.now() - snapshotFetchedAtMs > 60_000 ? (
            <Chip label="Stale" color="warning" size="small" variant="outlined" />
          ) : (
            <Chip label="Fresh" color="success" size="small" variant="outlined" />
          )}
        </Box>
      )}

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Draw Mode</InputLabel>
        <Select
          label="Draw Mode"
          value={geometryMode}
          onChange={(e) => {
            const next = String(e.target.value || 'rect') as GeometryMode;
            setGeometryMode(next);
            setPolyDraft(null);
            setPolyDragIndex(null);
            setPolyDragOrigin(null);
            setRotateDragOrigin(null);
            setRotateCenter(null);
            setRotateStartAngle(null);
            setLineDraft(null);
            setLineDragIndex(null);
            setLineDragOrigin(null);
          }}
        >
          <MenuItem value="rect">Rectangle</MenuItem>
          <MenuItem value="poly">Polygon</MenuItem>
          <MenuItem value="line">Line</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Switch
            checked={tab === 'zones' ? showDisabledZones : showDisabledLines}
            onChange={(e) => {
              if (tab === 'zones') setShowDisabledZones(e.target.checked);
              else setShowDisabledLines(e.target.checked);
            }}
          />
        }
        label="Show disabled"
      />
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Angle Snap</InputLabel>
        <Select
          label="Angle Snap"
          value={String(rotationSnapStepDeg)}
          onChange={(e) => setRotationSnapStepDeg(Number(e.target.value || 0))}
        >
          <MenuItem value="0">Off</MenuItem>
          <MenuItem value="5">5 deg</MenuItem>
          <MenuItem value="10">10 deg</MenuItem>
          <MenuItem value="15">15 deg</MenuItem>
          <MenuItem value="30">30 deg</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMaximized}
      PaperProps={{
        sx: isMaximized ? { m: 0, width: '100%', height: '100%' } : undefined,
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6">Zone Editor (Snapshot)</Typography>
        <IconButton
          size="small"
          onClick={() => setIsMaximized((v) => !v)}
          aria-label={isMaximized ? 'Restore dialog size' : 'Maximize dialog'}
        >
          {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {header}
        {(snapshotLoading || zonesLoading || actionBusy) && <LinearProgress sx={{ mt: 2 }} />}

        {validation && validation.ok !== true && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Validation failed: {validation?.summary?.errors || 0} error(s). Fix before deploy.
          </Alert>
        )}
        {validation && validation.ok === true && validation?.summary?.warnings > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Validation warnings: {validation.summary.warnings}. You can still deploy.
          </Alert>
        )}
        {!geometryAuthoringReady && (
          <Alert severity={snapshotSource === 'forced' ? 'warning' : 'info'} sx={{ mt: 2 }}>
            Zone and line geometry editing is disabled until the editor is using
            {' '}<strong>Edge live framing</strong>. Fallback snapshots can drift from the live RTSP framing
            and save incorrect normalized coordinates.
          </Alert>
        )}

        <Box display="grid" gridTemplateColumns="360px 1fr 360px" gap={2} mt={2} alignItems="start">
          {/* Zone/Line list */}
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1">{tab === 'zones' ? 'Zones' : 'Lines'}</Typography>
            </Box>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v as EditorTab)}
              sx={{ mb: 1 }}
              variant="fullWidth"
            >
              <Tab value="zones" label="Zones" />
              <Tab value="lines" label="Lines" />
            </Tabs>
            <Divider sx={{ mb: 1 }} />
            {tab === 'zones' && (
              <Box display="flex" gap={1} alignItems="center" mb={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Apply template</InputLabel>
                  <Select
                    label="Apply template"
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(String(e.target.value || ''))}
                  >
                    {zoneTemplates.map((t) => (
                      <MenuItem key={t.template_id} value={t.template_id}>
                        {t.name}
                      </MenuItem>
                    ))}
                    {zoneTemplates.length === 0 && (
                      <MenuItem value="" disabled>
                        {zoneTemplatesLoading ? 'Loading…' : 'No templates yet'}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                <Tooltip title="Refresh templates">
                  <span>
                    <IconButton size="small" onClick={() => void refreshZoneTemplates()} disabled={zoneTemplatesLoading}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={applyTemplateToCurrentCamera}
                  disabled={!selectedTemplateId || zoneTemplatesLoading || !deviceId || !cameraId}
                >
                  Apply
                </Button>
              </Box>
            )}
            <List dense sx={{ maxHeight: 520, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {tab === 'zones' ? (
                <>
                  {zones.map((z) => (
                    <ListItemButton
                      key={z.id}
                      selected={z.id === selectedZoneId}
                      onClick={() => {
                        setSelectedZoneId(z.id);
                        setSelectedLineId('');
                      }}
                    >
                      <ListItemText
                        primary={
                          z.name
                            ? (z.zone_id ? `${z.name} (${z.zone_id})` : z.name)
                            : (z.zone_id || '(unnamed)')
                        }
                        secondary={`${z.zone_id || ''}${z.zone_id ? ' • ' : ''}${z.zone_type || z.type || 'work_area'}${
                          z.enabled === false ? ' (disabled)' : ''
                        }`}
                      />
                    </ListItemButton>
                  ))}
                  {zones.length === 0 && (
                    <Box p={2}>
                      <Typography variant="body2" color="text.secondary">
                        No zones for this camera yet. Drag on the snapshot to create one.
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {lines.map((l) => (
                    <ListItemButton
                      key={l.id}
                      selected={l.id === selectedLineId}
                      onClick={() => {
                        setSelectedLineId(l.id);
                        setSelectedZoneId('');
                      }}
                    >
                      <ListItemText
                        primary={l.name || '(unnamed)'}
                        secondary={`${l.line_id || ''}${l.line_id ? ' • ' : ''}${l.enabled === false ? 'disabled' : 'enabled'}`}
                      />
                    </ListItemButton>
                  ))}
                  {lines.length === 0 && (
                    <Box p={2}>
                      <Typography variant="body2" color="text.secondary">
                        No lines for this camera yet. Use Draw Mode: Line to create one.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </List>
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary">
                {tab === 'zones'
                  ? `Tip: drag to create. Click inside a zone to move; drag corners to resize; drag the orange handle to rotate${rotationSnapStepDeg > 0 ? ` (hold Shift to snap ${rotationSnapStepDeg} degrees)` : ''}.`
                  : 'Tip: choose Draw Mode: Line. Click two points to create; drag endpoints to adjust.'}
              </Typography>
            </Box>
          </Box>

          {/* Snapshot + canvas */}
          <Box>
            <Typography variant="subtitle1">Snapshot</Typography>
            <Divider sx={{ mb: 1 }} />
            <Box position="relative" border="1px solid" borderColor="divider" borderRadius={1} overflow="hidden">
              {snapshotDataUrl ? (
                <>
                  <img
                    ref={imgRef}
                    src={snapshotDataUrl}
                    alt="camera snapshot"
                    style={{ width: '100%', display: 'block', userSelect: 'none' }}
                    onLoad={() => draw()}
                  />
                  <canvas
                    ref={canvasRef}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      cursor: geometryAuthoringReady ? 'crosshair' : 'not-allowed',
                    }}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                  />
                </>
              ) : (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">
                    Snapshot not available for this camera yet.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Properties */}
          <Box>
            <Typography variant="subtitle1">{tab === 'zones' ? 'Selected Zone' : 'Selected Line'}</Typography>
            <Divider sx={{ mb: 1 }} />
            {tab === 'zones' ? (
              selectedZone ? (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Basic
                  </Typography>
                  <Button size="small" variant="text" onClick={() => setShowZoneAdvanced((v) => !v)}>
                    {showZoneAdvanced ? 'Hide advanced' : 'Advanced'}
                  </Button>
                </Box>
                <Alert severity="info">
                  Zones define geometry and descriptive context. Use policies to decide whether this zone is enforced as restricted or warning.
                </Alert>
                <TextField
                  label="Name"
                  size="small"
                  autoFocus={pendingNameFocus && selectedZone._local_new}
                  value={selectedZone.name || ''}
                  onChange={(e) => {
                    const nm = e.target.value;
                    // Keep auto-generated IDs synced with the typed name until the
                    // operator explicitly edits Zone ID in Advanced mode.
                    if (selectedZone._local_new && (selectedZone._auto_zone_id !== false)) {
                      const zid = slugify(nm);
                      updateSelected({ name: nm, zone_id: zid, _auto_zone_id: true });
                    } else {
                      updateSelected({ name: nm });
                    }
                    setPendingNameFocus(false);
                  }}
                  helperText="Display label shown in UI and overlays. Safe to edit anytime."
                />
                <TextField
                  label="Zone ID"
                  size="small"
                  value={selectedZone.zone_id || ''}
                  onChange={(e) => {
                    // Prevent accidental edits unless the operator explicitly enables Advanced mode.
                    if (!showZoneAdvanced) return;
                    updateSelected({ zone_id: e.target.value, _auto_zone_id: false });
                  }}
                  InputProps={{ readOnly: !showZoneAdvanced }}
                  helperText={
                    showZoneAdvanced
                      ? 'Stable ID used by policies/incidents. Changing it can break policy matching.'
                      : 'Stable ID used by policies/incidents. Click Advanced to edit (usually don’t).'
                  }
                />
                <FormControl size="small">
                  <InputLabel>Color</InputLabel>
                  <Select
                    label="Color"
                    value={(() => {
                      const cur = String(selectedZone.color || '#00FFFF').trim();
                      const hit = SUPPORTED_ZONE_COLORS.find((c) => c.value.toUpperCase() === cur.toUpperCase());
                      return hit ? hit.value : cur;
                    })()}
                    onChange={(e) => updateSelected({ color: String(e.target.value || '#00FFFF') })}
                  >
                    {SUPPORTED_ZONE_COLORS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.name}
                      </MenuItem>
                    ))}
                    {(() => {
                      const cur = String(selectedZone.color || '').trim();
                      if (!cur) return null;
                      const hit = SUPPORTED_ZONE_COLORS.find((c) => c.value.toUpperCase() === cur.toUpperCase());
                      if (hit) return null;
                      return (
                        <MenuItem value={cur}>
                          {`Unsupported (${cur})`}
                        </MenuItem>
                      );
                    })()}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch checked={selectedZone.enabled !== false} onChange={(e) => updateSelected({ enabled: e.target.checked })} />}
                  label="Enabled"
                />
                <FormControl size="small">
                  <InputLabel>Zone class</InputLabel>
                  <Select
                    label="Zone class"
                    value={(selectedZone as any).zone_class || ((selectedZone as any).restricted ? 'high_risk' : 'general')}
                    onChange={(e) => {
                      const cls = String(e.target.value || 'general');
                      const patch: any = { zone_class: cls, restricted: false };
                      updateSelected(patch);
                    }}
                  >
                    <MenuItem value="general">general</MenuItem>
                    <MenuItem value="high_risk">high_risk</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Risk level</InputLabel>
                  <Select
                    label="Risk level"
                    value={(selectedZone as any).risk_level || ''}
                    onChange={(e) => updateSelected({ risk_level: String(e.target.value || '') } as any)}
                  >
                    <MenuItem value="">(unset)</MenuItem>
                    <MenuItem value="low">low</MenuItem>
                    <MenuItem value="medium">medium</MenuItem>
                    <MenuItem value="high">high</MenuItem>
                    <MenuItem value="critical">critical</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Zone type"
                  size="small"
                  value={selectedZone.zone_type || selectedZone.type || 'work_area'}
                  onChange={(e) => updateSelected({ zone_type: e.target.value, type: e.target.value as any })}
                  helperText='Examples: work_area, vehicle_zone, office. Enforcement mode is set in policies.'
                />
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    color={selectedZoneIsPolygon ? 'warning' : 'info'}
                    label={selectedZoneIsPolygon ? 'Polygon geometry' : 'Rectangle geometry'}
                  />
                  {selectedZoneIsPolygon && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${Math.round(
                        polygonAngleDeg(
                          ((selectedZone as any).polygon_norm as number[][]).map((p) => ({ x: p[0], y: p[1] }))
                        )
                      )} deg`}
                    />
                  )}
                </Box>
                <Alert severity="info" variant="outlined">
                  Polygon editing works. Select a zone, convert it to polygon if needed, drag the white vertices to reshape,
                  and use the orange rotate handle or the rotate buttons below.
                </Alert>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {selectedZoneIsPolygon ? (
                    <Button variant="outlined" size="small" onClick={resetSelectedZoneToRectangle}>
                      Use rectangle geometry
                    </Button>
                  ) : (
                    <Button variant="outlined" size="small" onClick={convertSelectedZoneToPolygon}>
                      Convert to polygon
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => rotateSelectedZoneByDeg(-(rotationSnapStepDeg || 15))}
                  >
                    Rotate -{rotationSnapStepDeg || 15} deg
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => rotateSelectedZoneByDeg(rotationSnapStepDeg || 15)}
                  >
                    Rotate +{rotationSnapStepDeg || 15} deg
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Tip: click a zone once to select it. White points are draggable polygon vertices; the orange handle above
                  the zone rotates it.
                </Typography>
                <TextField
                  label="bbox_norm"
                  size="small"
                  value={JSON.stringify(selectedZone.bbox_norm || [])}
                  onChange={(e) => {
                    try {
                      const b = JSON.parse(e.target.value);
                      if (Array.isArray(b) && b.length === 4) updateSelected({ bbox_norm: b });
                    } catch {
                      // ignore
                    }
                  }}
                />
                <TextField
                  label="polygon_norm"
                  size="small"
                  value={JSON.stringify(((selectedZone as any).polygon_norm as any) || [])}
                  onChange={(e) => {
                    try {
                      const p = JSON.parse(e.target.value);
                      if (Array.isArray(p) && p.length >= 3) {
                        updateSelected({ polygon_norm: p as any, bbox_norm: bboxFromPoly(p) });
                      }
                    } catch {
                      // ignore
                    }
                  }}
                  helperText="Optional polygon points. If present, they override bbox drawing."
                />

                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={openSaveTemplateDialog}
                  disabled={!selectedZone}
                >
                  Save as template
                </Button>

                <Divider sx={{ my: 1 }} />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={deleteSelected}
                  disabled={zonesLoading}
                >
                  Delete Zone
                </Button>
              </Box>
              ) : (
              <Typography variant="body2" color="text.secondary">
                Select a zone from the list.
              </Typography>
              )
            ) : selectedLine ? (
              <Box display="flex" flexDirection="column" gap={1.5}>
                <TextField
                  label="Line ID"
                  size="small"
                  value={selectedLine.line_id || ''}
                  onChange={(e) => updateSelectedLine({ line_id: e.target.value })}
                />
                <TextField
                  label="Name"
                  size="small"
                  value={selectedLine.name || ''}
                  onChange={(e) => {
                    const nm = e.target.value;
                    if ((selectedLine as any)._local_new && (!selectedLine.line_id || selectedLine.line_id.trim() === '')) {
                      updateSelectedLine({ name: nm, line_id: slugify(nm) });
                    } else {
                      updateSelectedLine({ name: nm });
                    }
                  }}
                />
                <FormControl size="small">
                  <InputLabel>Color</InputLabel>
                  <Select
                    label="Color"
                    value={(() => {
                      const cur = String(selectedLine.color || '#00FFFF').trim();
                      const hit = SUPPORTED_ZONE_COLORS.find((c) => c.value.toUpperCase() === cur.toUpperCase());
                      return hit ? hit.value : cur;
                    })()}
                    onChange={(e) => updateSelectedLine({ color: String(e.target.value || '#00FFFF') })}
                  >
                    {SUPPORTED_ZONE_COLORS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Thickness</InputLabel>
                  <Select
                    label="Thickness"
                    value={(() => {
                      const cur = Number((selectedLine as any).thickness_px ?? 6);
                      const hit = SUPPORTED_LINE_THICKNESS.find((t) => t.value === cur);
                      return hit ? hit.value : 6;
                    })()}
                    onChange={(e) => updateSelectedLine({ thickness_px: Number(e.target.value || 6) } as any)}
                  >
                    {SUPPORTED_LINE_THICKNESS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch checked={selectedLine.enabled !== false} onChange={(e) => updateSelectedLine({ enabled: e.target.checked })} />}
                  label="Enabled"
                />
                <TextField
                  label="points_norm"
                  size="small"
                  value={JSON.stringify(selectedLine.points_norm || [])}
                  onChange={(e) => {
                    try {
                      const p = JSON.parse(e.target.value);
                      if (Array.isArray(p) && p.length === 2) updateSelectedLine({ points_norm: p });
                    } catch {
                      // ignore
                    }
                  }}
                />
                <Divider sx={{ my: 1 }} />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={deleteSelectedLine}
                  disabled={linesLoading}
                >
                  Delete Line
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a line from the list.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box mr="auto" ml={1} display="flex" alignItems="center" gap={1}>
          {hasUnsavedEdits ? (
            <>
              <Chip size="small" color="warning" label="Unsaved edits" />
              {lastDraftSavedAtMs && (
                <Typography variant="caption" color="text.secondary">
                  Last saved at {new Date(lastDraftSavedAtMs).toLocaleTimeString()}
                </Typography>
              )}
            </>
          ) : draftSaveState === 'saved' && lastDraftSavedAtMs ? (
            <>
              <Chip size="small" color="success" label="Draft saved" />
              <Typography variant="caption" color="text.secondary">
                Saved at {new Date(lastDraftSavedAtMs).toLocaleTimeString()}
              </Typography>
            </>
          ) : draftSaveState === 'error' ? (
            <Chip size="small" color="error" label="Last save failed" />
          ) : (
            <Chip size="small" variant="outlined" label="No draft saved yet" />
          )}
        </Box>
        <Button onClick={onClose}>Close</Button>
        <Button variant="outlined" startIcon={<SaveIcon />} onClick={saveDraft} disabled={!cameraId || zonesLoading || actionBusy}>
          Save Draft
        </Button>
        <Button
          variant="contained"
          startIcon={
            actionBusy ? <CircularProgress size={16} color="inherit" /> : <DeployIcon />
          }
          onClick={deploy}
          disabled={!cameraId || zonesLoading || actionBusy}
        >
          {action === 'deploying' ? 'Deploying…' : action === 'validating' ? 'Validating…' : 'Validate + Deploy'}
        </Button>
      </DialogActions>

      <Dialog open={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Save zone as template</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Template name"
            value={saveTemplateName}
            onChange={(e) => setSaveTemplateName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={saveTemplateDescription}
            onChange={(e) => setSaveTemplateDescription(e.target.value)}
            multiline
            minRows={2}
          />
          <Alert severity="info">
            Templates store geometry + defaults. Applying a template creates a new local zone you can tweak before saving/deploying.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveSelectedZoneAsTemplate} disabled={zoneTemplatesLoading}>
            {zoneTemplatesLoading ? 'Saving…' : 'Save template'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
